  (async () => {
    'use strict';

    const selector = document.getElementById('mp-tax-selector');
    if (!selector) return;
    if (window.MPCountryReady && await window.MPCountryReady) return;

    const reconcileDuplicateCartCookies = () => {
      const cartCookieCount = document.cookie
        .split(';')
        .map((part) => part.trim())
        .filter((part) => part.indexOf('cart=') === 0)
        .length;
      if (cartCookieCount < 2) return;

      const canonicalHost = window.location.hostname
        .toLowerCase()
        .replace(/^www\./, '');
      if (canonicalHost !== 'miniminipower.com') return;

      const secureAttribute = window.location.protocol === 'https:' ? '; Secure' : '';
      document.cookie = `cart=; Path=/; Max-Age=0; SameSite=Lax${secureAttribute}; Domain=.${canonicalHost}`;
    };

    reconcileDuplicateCartCookies();

    const country = (selector.dataset.country || 'DE').toUpperCase();
    const language = (selector.dataset.language || 'de').toLowerCase();
    const currency = (selector.dataset.currency || 'EUR').toUpperCase();
    const marketId = selector.dataset.marketId || 'default';
    const storageKey = 'mp_customer_type_v2';
    const initialCartItemCount = Math.max(
      0,
      Number.parseInt(selector.dataset.cartItemCount || '0', 10) || 0
    );
    const onCartPage = /\/cart(?:\/|$)/.test(window.location.pathname);
    const countrySelect = selector.querySelector('#mp-tax-country');
    const buttons = Array.from(selector.querySelectorAll('[data-mp-tax-role]'));
    const detailsPanel = selector.querySelector('[data-mp-tax-details]');
    const trustedDetails = selector.querySelector('[data-mp-details-trusted]');
    const bankDetails = selector.querySelector('[data-mp-details-bank]');
    const roleDetails = selector.querySelector('[data-mp-details-role]');
    const contextNode = selector.querySelector('[data-mp-tax-context]');
    const contextTitleNode = selector.querySelector('[data-mp-tax-context-title]');
    const priceNode = selector.querySelector('[data-mp-tax-price]');
    const statusNode = selector.querySelector('[data-mp-tax-status]');
    const trustedLink = selector.querySelector('[data-mp-trusted-rating]');
    const bankDiscountButton = selector.querySelector('[data-mp-bank-discount]');
    const trustedScoreNode = selector.querySelector('[data-mp-trusted-score]');
    const trustedDetailsScoreNode = selector.querySelector('[data-mp-trusted-details-score]');
    const trustedDetailsCountNode = selector.querySelector('[data-mp-trusted-details-count]');
    const trustedDivider = selector.querySelector('[data-mp-trusted-divider]');
    const detailsTriggers = Array.from(selector.querySelectorAll('[aria-controls="mp-tax-details"]'));
    const byVariant = new Map();
    const byHandle = new Map();
    const defaultVariantByHandle = new Map();
    const variantIdsByHandle = new Map();
    const selectedRuleSnapshotHandles = new Set();
    const completeRulePayloadHandles = new Set();
    const productRuleLoads = new Map();
    const productRuleRetryAfter = new Map();
    const collectionRuleBatchLoads = new Map();
    const productRuleQueue = [];
    const maxConcurrentProductRuleLoads = 3;
    const productRuleSessionCachePrefix = 'mp_tax_rules_v2:';
    const collectionRuleBatchSessionCachePrefix = 'mp_tax_collection_batch_v1:';
    const productRuleSessionContext = `${encodeURIComponent(marketId)}:${country}:${currency}:`;
    const productRuleSessionCacheTtl = 5 * 60 * 1000;
    let activeProductRuleLoads = 0;
    const conflictedVariantIds = new Set();
    const vatRates = {
      AT: 20,
      BE: 21,
      BG: 20,
      CZ: 21,
      DE: 19,
      DK: 25,
      EE: 24,
      ES: 21,
      FI: 25.5,
      FR: 20,
      GR: 24,
      HR: 25,
      HU: 27,
      IE: 23,
      IT: 10,
      LT: 21,
      LU: 17,
      LV: 21,
      NL: 21,
      PL: 23,
      PT: 23,
      RO: 21,
      SE: 25,
      SI: 22,
      SK: 23,
    };

    const addHandleMapping = (handle, mapping) => {
      if (!handle) return;
      const existing = byHandle.get(handle) || [];
      const signature = `${mapping.sourceVariantId}:${mapping.standardVariantId}`;
      if (!existing.some((candidate) => (
        `${candidate.sourceVariantId}:${candidate.standardVariantId}` === signature
      ))) {
        existing.push(mapping);
        byHandle.set(handle, existing);
      }
    };

    const normalizedId = (value) => {
      const id = String(value || '');
      return /^\d+$/.test(id) ? id : '';
    };

    const normalizedMoney = (value, fallback = null) => {
      if (value === null || value === undefined || value === '') return fallback;
      const cents = Number(value);
      return Number.isFinite(cents) && cents >= 0 ? Math.round(cents) : fallback;
    };

    const expandCompactRule = (rule) => {
      if (!Array.isArray(rule)) return rule;

      if (rule[0] === 'g' && rule.length === 5) {
        return {
          enabled: true,
          mode: 'gross_inclusive_single',
          side: 'single',
          schemaVersion: 1,
          variantId: rule[1],
          handle: rule[2],
          price: rule[3],
          compareAtPrice: rule[4],
          peerVariantId: null,
        };
      }

      if (
        rule[0] === 'd'
        && (rule[1] === 's' || rule[1] === 't')
        && rule.length === 10
      ) {
        const side = rule[1] === 's' ? 'source_de0' : 'standard_tax';
        return {
          enabled: true,
          mode: 'de0_twin',
          side,
          schemaVersion: 1,
          variantId: rule[2],
          handle: rule[3],
          price: rule[4],
          compareAtPrice: rule[5],
          peerVariantId: rule[6],
          peerHandle: rule[7],
          peerPrice: rule[8],
          peerCompareAtPrice: rule[9],
          peerSide: side === 'source_de0' ? 'standard_tax' : 'source_de0',
          peerSchemaVersion: 1,
        };
      }

      return null;
    };

    const compactRuleVariantId = (rule) => {
      if (!Array.isArray(rule)) return '';
      if (rule[0] === 'g' && rule.length === 5) return normalizedId(rule[1]);
      if (rule[0] === 'd' && rule.length === 10) return normalizedId(rule[2]);
      return '';
    };

    const normalizeRule = (rule) => {
      if (!rule || rule.enabled !== true) return null;

      const mode = String(rule.mode || '');
      const side = String(rule.side || '');
      const schemaVersion = Number(rule.schemaVersion);
      const variantId = normalizedId(rule.variantId);
      const peerVariantId = normalizedId(rule.peerVariantId);
      const handle = String(rule.handle || '').toLowerCase();
      const peerHandle = String(rule.peerHandle || '').toLowerCase();
      const price = normalizedMoney(rule.price);
      const compareAtPrice = normalizedMoney(rule.compareAtPrice, 0);
      const peerPrice = normalizedMoney(rule.peerPrice);
      const peerCompareAtPrice = normalizedMoney(rule.peerCompareAtPrice, 0);

      if (!variantId || schemaVersion !== 1 || price === null) return null;

      if (mode === 'gross_inclusive_single') {
        if (side !== 'single' || peerVariantId) return null;

        return {
          sku: String(rule.sku || ''),
          taxMode: mode,
          schemaVersion,
          sourceHandle: handle,
          standardHandle: handle,
          sourceVariantId: variantId,
          standardVariantId: variantId,
          sourcePrice: price,
          standardPrice: price,
          sourceCompareAtPrice: compareAtPrice,
          standardCompareAtPrice: compareAtPrice,
        };
      }

      if (mode !== 'de0_twin' || !peerVariantId || peerVariantId === variantId) return null;
      if (side !== 'source_de0' && side !== 'standard_tax') return null;
      if (Number(rule.peerSchemaVersion) !== schemaVersion || peerPrice === null) return null;

      const expectedPeerSide = side === 'source_de0' ? 'standard_tax' : 'source_de0';
      if (String(rule.peerSide || '') !== expectedPeerSide) return null;

      const currentIsSource = side === 'source_de0';
      return {
        sku: String((currentIsSource ? rule.sku : rule.peerSku) || rule.sku || rule.peerSku || ''),
        taxMode: mode,
        schemaVersion,
        sourceHandle: currentIsSource ? handle : peerHandle,
        standardHandle: currentIsSource ? peerHandle : handle,
        sourceVariantId: currentIsSource ? variantId : peerVariantId,
        standardVariantId: currentIsSource ? peerVariantId : variantId,
        sourcePrice: currentIsSource ? price : peerPrice,
        standardPrice: currentIsSource ? peerPrice : price,
        sourceCompareAtPrice: currentIsSource ? compareAtPrice : peerCompareAtPrice,
        standardCompareAtPrice: currentIsSource ? peerCompareAtPrice : compareAtPrice,
      };
    };

    const mappingSignature = (mapping) => JSON.stringify([
      mapping.taxMode,
      mapping.schemaVersion,
      mapping.sourceVariantId,
      mapping.standardVariantId,
      mapping.sourceHandle,
      mapping.standardHandle,
      mapping.sourcePrice,
      mapping.standardPrice,
      mapping.sourceCompareAtPrice,
      mapping.standardCompareAtPrice,
    ]);

    const registerRule = (rule) => {
      const normalized = normalizeRule(expandCompactRule(rule));
      if (!normalized) return false;

      const ids = [normalized.sourceVariantId, normalized.standardVariantId];
      if (ids.some((id) => conflictedVariantIds.has(id))) return false;

      const existing = ids
        .map((id) => byVariant.get(id))
        .find(Boolean);

      if (existing && mappingSignature(existing) !== mappingSignature(normalized)) {
        const conflictIds = new Set([
          ...ids,
          existing.sourceVariantId,
          existing.standardVariantId,
        ]);
        conflictIds.forEach((id) => {
          conflictedVariantIds.add(id);
          byVariant.delete(id);
        });
        console.error(
          '[Mini Power Tax] Conflicting variant metafield rules were ignored.',
          Array.from(conflictIds)
        );
        return false;
      }

      const mapping = existing || normalized;
      ids.forEach((id) => byVariant.set(id, mapping));
      addHandleMapping(mapping.sourceHandle, mapping);
      addHandleMapping(mapping.standardHandle, mapping);
      return true;
    };

    const registerRulePayload = (payload) => {
      if (Array.isArray(payload)) {
        payload.forEach(registerRule);
        return;
      }

      if (!payload || typeof payload !== 'object') return;
      if (payload.variantId) registerRule(payload);
      if (payload.mpTax) registerRule(payload.mpTax);
      if (Array.isArray(payload.rules)) payload.rules.forEach(registerRule);
      if (Array.isArray(payload.variants)) {
        payload.variants.forEach((variant) => {
          if (variant && variant.mpTax) registerRule(variant.mpTax);
        });
      }
    };

    const rememberDefaultVariant = (payload, fallbackVariantId = '') => {
      const defaultVariantId = normalizedId(
        payload && !Array.isArray(payload) ? payload.defaultVariantId : fallbackVariantId
      ) || normalizedId(fallbackVariantId);

      let handle = String(
        payload && !Array.isArray(payload) ? payload.handle || '' : ''
      ).toLowerCase();

      if (!handle) {
        const entries = Array.isArray(payload)
          ? payload
          : (Array.isArray(payload?.variants) ? payload.variants : []);
        const defaultEntry = entries.find((entry) => normalizedId(
          entry?.variantId || entry?.id || entry?.mpTax?.variantId
        ) === defaultVariantId);
        handle = String(defaultEntry?.handle || defaultEntry?.mpTax?.handle || '').toLowerCase();
      }

      if (!handle) return;

      if (payload && !Array.isArray(payload)) {
        if (payload.selectedOnly === true) {
          selectedRuleSnapshotHandles.add(handle);
          completeRulePayloadHandles.delete(handle);
        } else {
          selectedRuleSnapshotHandles.delete(handle);
          completeRulePayloadHandles.add(handle);
        }
      }

      if (payload && !Array.isArray(payload)) {
        const entries = Array.isArray(payload.rules)
          ? payload.rules
          : (Array.isArray(payload.variants) ? payload.variants : []);
        const variantIds = entries
          .map((entry) => (
            compactRuleVariantId(entry)
            || normalizedId(entry?.id || entry?.variantId || entry?.mpTax?.variantId)
          ))
          .filter((variantId) => variantId && byVariant.has(variantId));
        if (variantIds.length > 0) variantIdsByHandle.set(handle, variantIds);
      }

      if (defaultVariantId && byVariant.has(defaultVariantId)) {
        defaultVariantByHandle.set(handle, defaultVariantId);
      }
    };

    const validProductRulePayload = (payload, handle) => Boolean(
      payload
      && String(payload.handle || '').toLowerCase() === handle
      && Array.isArray(payload.variants)
    );

    const readCachedProductRules = (handle) => {
      const cacheKey = `${productRuleSessionCachePrefix}${productRuleSessionContext}${handle}`;
      try {
        const raw = window.sessionStorage.getItem(cacheKey);
        if (!raw) return null;
        const entry = JSON.parse(raw);
        if (
          !entry
          || Date.now() - Number(entry.cachedAt || 0) > productRuleSessionCacheTtl
          || !validProductRulePayload(entry.payload, handle)
        ) {
          window.sessionStorage.removeItem(cacheKey);
          return null;
        }
        return entry.payload;
      } catch (error) {
        return null;
      }
    };

    const writeCachedProductRules = (handle, payload) => {
      try {
        window.sessionStorage.setItem(
          `${productRuleSessionCachePrefix}${productRuleSessionContext}${handle}`,
          JSON.stringify({ cachedAt: Date.now(), payload })
        );
      } catch (error) {
        // A blocked or full session cache must never affect tax correctness.
      }
    };

    const normalizedCollectionBatchUrl = (value) => {
      try {
        const url = new URL(String(value || ''), window.location.origin);
        const page = Number.parseInt(url.searchParams.get('page') || '1', 10);
        if (
          url.origin !== window.location.origin
          || !/(?:^|\/)collections\//.test(url.pathname)
          || url.searchParams.get('view') !== 'mp-tax'
          || !Number.isInteger(page)
          || page < 1
        ) return '';
        url.hash = '';
        url.searchParams.sort();
        return url.href;
      } catch (error) {
        return '';
      }
    };

    const collectionHandleFromBatchUrl = (value) => {
      const normalizedUrl = normalizedCollectionBatchUrl(value);
      if (!normalizedUrl) return '';
      try {
        const pathParts = new URL(normalizedUrl).pathname.split('/').filter(Boolean);
        const collectionsIndex = pathParts.lastIndexOf('collections');
        if (collectionsIndex < 0 || collectionsIndex + 1 >= pathParts.length) return '';
        return decodeURIComponent(pathParts[collectionsIndex + 1]).trim().toLowerCase();
      } catch (error) {
        return '';
      }
    };

    const validCompactRuleId = (value) => (
      (typeof value === 'number' && Number.isSafeInteger(value) && value > 0)
      || (typeof value === 'string' && /^[1-9]\d*$/.test(value))
    );

    const validCompactMoney = (value, { nullable = false } = {}) => (
      (nullable && value === null)
      || (typeof value === 'number' && Number.isSafeInteger(value) && value >= 0)
    );

    const validCompactRuleForProduct = (rule, productHandle) => {
      if (rule === null) return true;
      if (!Array.isArray(rule)) return false;
      if (rule[0] === 'g') {
        return Boolean(
          rule.length === 5
          && validCompactRuleId(rule[1])
          && rule[2] === productHandle
          && validCompactMoney(rule[3])
          && validCompactMoney(rule[4], { nullable: true })
        );
      }
      if (rule[0] === 'd') {
        return Boolean(
          rule.length === 10
          && (rule[1] === 's' || rule[1] === 't')
          && validCompactRuleId(rule[2])
          && rule[3] === productHandle
          && validCompactMoney(rule[4])
          && validCompactMoney(rule[5], { nullable: true })
          && validCompactRuleId(rule[6])
          && typeof rule[7] === 'string'
          && rule[7].trim() !== ''
          && validCompactMoney(rule[8])
          && validCompactMoney(rule[9], { nullable: true })
        );
      }
      return false;
    };

    const validCollectionRuleBatchPayload = (payload, batchUrl, expectedCollectionHandle) => {
      const normalizedUrl = normalizedCollectionBatchUrl(batchUrl);
      if (!normalizedUrl) return false;
      const expectedPage = Number.parseInt(new URL(normalizedUrl).searchParams.get('page') || '1', 10);
      const expectedHandle = String(
        expectedCollectionHandle || collectionHandleFromBatchUrl(normalizedUrl)
      ).trim().toLowerCase();
      const seenProductHandles = new Set();
      return Boolean(
        payload
        && expectedHandle
        && payload.collectionHandle === expectedHandle
        && Number(payload.page) === expectedPage
        && Number(payload.pageSize) === 12
        && Array.isArray(payload.products)
        && payload.products.length <= 12
        && payload.products.every((productPayload) => (
          productPayload
          && typeof productPayload.handle === 'string'
          && productPayload.handle.trim() !== ''
          && productPayload.handle === productPayload.handle.trim().toLowerCase()
          && !seenProductHandles.has(productPayload.handle)
          && Boolean(seenProductHandles.add(productPayload.handle))
          && validCompactRuleId(productPayload.defaultVariantId)
          && typeof productPayload.price_varies === 'boolean'
          && Array.isArray(productPayload.rules)
          && productPayload.rules.length > 0
          && productPayload.rules.every((rule) => (
            validCompactRuleForProduct(rule, productPayload.handle)
          ))
        ))
      );
    };

    const registerCollectionRuleBatch = (payload) => {
      payload.products.forEach((productPayload) => {
        registerRulePayload(productPayload);
        rememberDefaultVariant(productPayload);
      });
    };

    const collectionRuleBatchCacheKey = (batchUrl) => (
      `${collectionRuleBatchSessionCachePrefix}${productRuleSessionContext}${encodeURIComponent(batchUrl)}`
    );

    const readCachedCollectionRuleBatch = (batchUrl, expectedCollectionHandle) => {
      const cacheKey = collectionRuleBatchCacheKey(batchUrl);
      try {
        const raw = window.sessionStorage.getItem(cacheKey);
        if (!raw) return null;
        const entry = JSON.parse(raw);
        if (
          !entry
          || Date.now() - Number(entry.cachedAt || 0) > productRuleSessionCacheTtl
          || !validCollectionRuleBatchPayload(entry.payload, batchUrl, expectedCollectionHandle)
        ) {
          window.sessionStorage.removeItem(cacheKey);
          return null;
        }
        return entry.payload;
      } catch (error) {
        return null;
      }
    };

    const writeCachedCollectionRuleBatch = (batchUrl, payload) => {
      try {
        window.sessionStorage.setItem(
          collectionRuleBatchCacheKey(batchUrl),
          JSON.stringify({ cachedAt: Date.now(), payload })
        );
      } catch (error) {
        // A blocked or full session cache must never affect tax correctness.
      }
    };

    const nodesWithin = (scope, selectorText) => {
      const nodes = [];
      if (scope instanceof Element && scope.matches(selectorText)) nodes.push(scope);
      if (scope && typeof scope.querySelectorAll === 'function') {
        nodes.push(...scope.querySelectorAll(selectorText));
      }
      return nodes;
    };

    const hydrateTaxRules = (scope = document) => {
      nodesWithin(scope, '[data-mp-tax-rule]').forEach((node) => {
        if (node.dataset.mpTaxRuleHydrated === 'true') return;
        node.dataset.mpTaxRuleHydrated = 'true';
        try {
          registerRulePayload(JSON.parse(node.getAttribute('data-mp-tax-rule') || 'null'));
        } catch (error) {
          console.error('[Mini Power Tax] Invalid variant rule attribute was ignored.', error);
        }
      });

      nodesWithin(scope, 'script[type="application/json"][data-mp-tax-rules]').forEach((node) => {
        if (node.dataset.mpTaxRulesHydrated === 'true') return;
        const raw = node.textContent || '';

        node.dataset.mpTaxRulesHydrated = 'true';
        try {
          const payload = JSON.parse(raw || 'null');
          if (payload && payload.price_varies === true) {
            node.dataset.mpTaxPriceVaries = 'true';
          }
          registerRulePayload(payload);
          rememberDefaultVariant(
            payload,
            node.getAttribute('data-mp-tax-default-variant-id') || ''
          );
        } catch (error) {
          console.error('[Mini Power Tax] Invalid local variant rule JSON was ignored.', error);
        }
      });
    };

    let role = 'private';
    try {
      const storedRole = window.localStorage.getItem(storageKey);
      if (storedRole === 'private' || storedRole === 'business') role = storedRole;
    } catch (error) {
      console.warn('[Mini Power Tax] Customer type could not be read from local storage.', error);
    }

    // Idealo consumer links explicitly select gross private-customer prices.
    // Persist the choice so the cart and checkout keep the same tax mapping.
    if (new URL(window.location.href).searchParams.get('mp_customer_type') === 'private') {
      role = 'private';
      try { window.localStorage.setItem(storageKey, role); } catch (_) {}
    }

    let syncPromise = null;
    let scheduledSync = null;
    let ignoreRefreshEventsUntil = 0;
    let checkoutInFlight = false;
    let mappedAddPromise = null;
    let latestCart = null;
    let knownCartItemCount = initialCartItemCount;
    let activeDetailsTrigger = null;
    const headerMedia = window.matchMedia('(max-width: 767px)');

    const mountSelector = () => {
      const header = document.querySelector('.t4s-section-header');
      const wrapper = header && header.querySelector('.t4s-header__wrapper');
      const middle = wrapper && wrapper.querySelector('.t4s-section-header__mid');
      const headerParent = header && header.parentNode;
      if (!wrapper || !middle || !headerParent) return;

      if (headerMedia.matches) {
        if (selector.parentNode !== wrapper || selector.nextElementSibling !== middle) {
          wrapper.insertBefore(selector, middle);
        }
      } else if (selector.parentNode !== headerParent || selector.nextElementSibling !== header) {
        headerParent.insertBefore(selector, header);
      }

      header.classList.add('has-mp-tax-selector');
      selector.classList.toggle('is-mobile-header', headerMedia.matches);
      selector.classList.add('is-mounted');
      selector.hidden = false;
    };

    const route = (path) => {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const relativePath = String(path).replace(/^\//, '');
      const url = `${root.replace(/\/?$/, '/')}${relativePath}`;
      if (
        !window.location.hostname.endsWith('.myshopify.com')
        || !/^cart(?:[/.]|$)/.test(relativePath)
      ) return url;

      const currentParams = new URLSearchParams(window.location.search);
      const previewParams = new URLSearchParams({
        _fd: currentParams.get('_fd') || '0',
        pb: currentParams.get('pb') || '0',
      });
      if (currentParams.has('preview_theme_id')) {
        previewParams.set('preview_theme_id', currentParams.get('preview_theme_id'));
      }
      return `${url}${url.includes('?') ? '&' : '?'}${previewParams}`;
    };

    const text = (german, english) => (language === 'de' ? german : english);
    const contextTemplate = (name) => (
      selector.querySelector(`[data-mp-tax-context-template="${name}"]`)
    );
    const normalizedTemplateText = (template) => (
      template?.content?.textContent?.replace(/\s+/g, ' ').trim() || ''
    );
    const positionDetails = (trigger) => {
      if (!detailsPanel || !trigger || detailsPanel.hidden) return;

      const triggerRect = trigger.getBoundingClientRect();
      const panelRect = detailsPanel.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
      const panelWidth = panelRect.width || Math.min(380, viewportWidth - 16);
      const margin = headerMedia.matches ? 8 : 10;
      const maxLeft = Math.max(margin, viewportWidth - panelWidth - margin);
      const preferredLeft = triggerRect.left;
      const left = Math.min(Math.max(preferredLeft, margin), maxLeft);
      const triggerCenter = triggerRect.left + triggerRect.width / 2;
      const arrowLeft = Math.min(
        Math.max(triggerCenter - left - 6, 18),
        Math.max(18, panelWidth - 29)
      );
      const top = triggerRect.bottom + 7;

      detailsPanel.style.setProperty('--mp-tax-details-top', `${Math.max(8, top)}px`);
      detailsPanel.style.setProperty('--mp-tax-details-left', `${left}px`);
      detailsPanel.style.setProperty('--mp-tax-details-arrow-left', `${arrowLeft}px`);
    };
    const closeDetails = ({ restoreFocus = false } = {}) => {
      if (!detailsPanel) return;

      const triggerToRestore = activeDetailsTrigger;
      detailsPanel.hidden = true;
      detailsPanel.removeAttribute('data-mp-details-mode');
      if (trustedDetails) trustedDetails.hidden = true;
      if (bankDetails) bankDetails.hidden = true;
      if (roleDetails) roleDetails.hidden = true;
      detailsTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      selector.classList.remove('is-details-open');
      activeDetailsTrigger = null;

      if (restoreFocus && triggerToRestore instanceof HTMLElement) {
        triggerToRestore.focus();
      }
    };
    const openDetails = (mode, trigger, { focusPanel = false } = {}) => {
      if (!detailsPanel || !trigger) return;

      if (detailsPanel.parentNode !== document.body) {
        document.body.appendChild(detailsPanel);
      }
      detailsTriggers.forEach((item) => item.setAttribute('aria-expanded', 'false'));
      if (trustedDetails) trustedDetails.hidden = mode !== 'trusted';
      if (bankDetails) bankDetails.hidden = mode !== 'bank';
      if (roleDetails) roleDetails.hidden = mode !== 'role';
      detailsPanel.dataset.mpDetailsMode = mode;
      detailsPanel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      selector.classList.add('is-details-open');
      activeDetailsTrigger = trigger;
      positionDetails(trigger);
      if (focusPanel) {
        window.requestAnimationFrame(() => {
          if (detailsPanel.hidden) return;
          try {
            detailsPanel.focus({ preventScroll: true });
          } catch (error) {
            detailsPanel.focus();
          }
        });
      }
    };
    const toggleDetails = (mode, trigger, options = {}) => {
      const isSameOpenPanel = detailsPanel
        && !detailsPanel.hidden
        && detailsPanel.dataset.mpDetailsMode === mode
        && activeDetailsTrigger === trigger;

      if (isSameOpenPanel) {
        closeDetails();
      } else {
        openDetails(mode, trigger, options);
      }
    };
    const hydrateTrustedRating = () => {
      if (!trustedLink || !trustedScoreNode) return;

      const source = document.querySelector(
        '[data-testid="trustbadge-floating-reviews-container"]'
      );
      if (!source) return;

      const rating = Array.from(source.querySelectorAll('span'))
        .map((node) => (node.textContent || '').trim())
        .find((value) => /^\d+[,.]\d+$/.test(value));
      if (!rating) return;

      const count = (
        source.querySelector('[data-testid="compact-rating-count"]')?.textContent || ''
      ).trim();
      const signature = `${rating}|${count}`;
      if (trustedLink.dataset.mpTrustedSignature === signature) return;

      trustedScoreNode.textContent = rating;
      if (trustedDetailsScoreNode) trustedDetailsScoreNode.textContent = rating;
      if (trustedDetailsCountNode) {
        const countLabel = trustedDetailsCountNode.dataset.label || '';
        trustedDetailsCountNode.textContent = count
          ? `${count} ${countLabel}`.trim()
          : '';
        trustedDetailsCountNode.hidden = !count;
      }
      trustedLink.dataset.mpTrustedSignature = signature;
      trustedLink.removeAttribute('data-mp-trusted-pending');
      trustedLink.removeAttribute('aria-hidden');
      trustedLink.disabled = false;
      if (trustedDivider) trustedDivider.removeAttribute('data-mp-trusted-pending');

      const countText = count
        ? text(`, ${count} Bewertungen`, `, ${count} reviews`)
        : '';
      trustedLink.setAttribute(
        'aria-label',
        text(
          `Hervorragend, ${rating} von 5, Trusted Shops${countText}`,
          `Excellent, ${rating} out of 5, Trusted Shops${countText}`
        )
      );
    };
    const locale = document.documentElement.lang || (language === 'de' ? 'de-DE' : 'en');
    const moneyFormatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const moneyWithCurrencyFormatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'code',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    const isGrossInclusive = (mapping) => (
      mapping?.taxMode === 'gross_inclusive_single'
    );

    const desiredMode = (mapping) => {
      if (isGrossInclusive(mapping)) return 'gross';
      // The Italian 10% checkout override is attached to the source collection.
      if (country === 'IT') return 'source';
      return country === 'DE' && role === 'private' ? 'source' : 'standard';
    };

    const desiredVariantId = (mapping) => (
      desiredMode(mapping) === 'source'
        ? mapping.sourceVariantId
        : mapping.standardVariantId
    );

    const vatRate = () => Number(vatRates[country]);

    const displayIncludesVat = (mapping) => {
      if (!Number.isFinite(vatRate())) return false;
      if (isGrossInclusive(mapping)) {
        return country === 'DE' || role === 'private';
      }
      return country === 'DE' ? role === 'business' : role === 'private';
    };

    const displayCents = (baseCents, mapping) => {
      const normalizedCents = Math.round(Number(baseCents) || 0);
      if (isGrossInclusive(mapping)) {
        // Standalone Wallbox 11 kW cable: catalog price includes German 19% VAT.
        // Only this variant rebases foreign consumer prices on the German net price.
        if (
          mapping.sourceVariantId === '54225611784539'
          && country !== 'DE'
          && role === 'private'
          && Number.isFinite(vatRate())
        ) {
          return Math.round(normalizedCents * (100 + vatRate()) / 119);
        }
        if (country !== 'DE' && role === 'business' && Number.isFinite(vatRate())) {
          return Math.round(normalizedCents * 100 / (100 + vatRate()));
        }
        return normalizedCents;
      }
      if (!displayIncludesVat(mapping)) return normalizedCents;
      return Math.round(normalizedCents * (100 + vatRate()) / 100);
    };

    const formatMoney = (cents, withCurrency = false) => (
      (withCurrency ? moneyWithCurrencyFormatter : moneyFormatter).format(
        Math.round(Number(cents) || 0) / 100
      )
    );

    const writeMoneyText = (node, cents, withCurrency = false) => {
      if (!(node instanceof Element)) return false;
      const nextText = formatMoney(cents, withCurrency);
      if ((node.textContent || '').trim() === nextText) return false;
      node.textContent = nextText;
      return true;
    };

    const markRenderedCents = (node, key, cents) => {
      if (!(node instanceof HTMLElement)) return;
      const nextValue = String(Math.round(Number(cents) || 0));
      if (node.dataset[key] !== nextValue) node.dataset[key] = nextValue;
    };

    const formatRate = () => {
      const rate = vatRate();
      if (!Number.isFinite(rate)) return '';
      if (Number.isInteger(rate)) return String(rate);
      return language === 'de' ? String(rate).replace('.', ',') : String(rate);
    };

    const mappingPrice = (mapping) => (
      desiredMode(mapping) === 'source' ? mapping.sourcePrice : mapping.standardPrice
    );

    const mappingCompareAtPrice = (mapping) => (
      desiredMode(mapping) === 'source'
        ? mapping.sourceCompareAtPrice
        : mapping.standardCompareAtPrice
    );

    const mappingForHandle = (handle, { requireUnique = true } = {}) => {
      const candidates = (byHandle.get(String(handle || '').toLowerCase()) || []).filter((candidate) => (
        byVariant.get(candidate.sourceVariantId) === candidate
        && byVariant.get(candidate.standardVariantId) === candidate
      ));
      if (candidates.length === 0) return null;
      if (requireUnique && candidates.length !== 1) return null;

      return candidates[0];
    };

    const productPageHandle = () => {
      const pageMatch = window.location.pathname.match(/(?:^|\/)products\/([^/]+)/);
      if (pageMatch) return decodeURIComponent(pageMatch[1]).toLowerCase();
      // Shopify draft previews use /products_preview instead of the product handle.
      if (/\/products_preview\/?$/.test(window.location.pathname)) {
        const pageRules = document.querySelector('[data-mp-tax-page-rules]');
        const mapping = byVariant.get(normalizedId(pageRules?.dataset.mpTaxDefaultVariantId));
        return mapping?.sourceHandle || '';
      }
      return '';
    };

    const mappingMatchesHandle = (mapping, handle) => {
      const normalizedHandle = String(handle || '').toLowerCase();
      return Boolean(
        mapping
        && normalizedHandle
        && (
          mapping.sourceHandle === normalizedHandle
          || mapping.standardHandle === normalizedHandle
        )
      );
    };

    const productMappingsForHandle = (handle) => {
      const normalizedHandle = String(handle || '').toLowerCase();
      const variantIds = variantIdsByHandle.get(normalizedHandle) || [];
      const seen = new Set();
      return variantIds
        .map((variantId) => byVariant.get(variantId))
        .filter((mapping) => {
          if (!mapping) return false;
          const signature = `${mapping.sourceVariantId}:${mapping.standardVariantId}`;
          if (seen.has(signature)) return false;
          seen.add(signature);
          return true;
        });
    };

    const mappingBasePriceForHandle = (mapping, handle) => {
      const normalizedHandle = String(handle || '').toLowerCase();
      if (mapping.sourceHandle === normalizedHandle) return mapping.sourcePrice;
      if (mapping.standardHandle === normalizedHandle) return mapping.standardPrice;
      return mappingPrice(mapping);
    };

    const lowestProductMappingForHandle = (handle) => (
      productMappingsForHandle(handle)
        .sort((left, right) => (
          mappingBasePriceForHandle(left, handle) - mappingBasePriceForHandle(right, handle)
        ))[0] || null
    );

    const displayedProductPriceRange = (handle) => {
      const prices = productMappingsForHandle(handle)
        .map((mapping) => displayCents(mappingPrice(mapping), mapping))
        .filter((price) => Number.isFinite(price));
      if (prices.length === 0) return null;
      return {
        min: Math.min(...prices),
        max: Math.max(...prices),
      };
    };

    const currentPageMapping = () => {
      const pageHandle = productPageHandle();
      if (!pageHandle) return null;

      const urlVariantId = normalizedId(
        new URL(window.location.href).searchParams.get('variant')
      );
      const urlMapping = byVariant.get(urlVariantId);
      if (mappingMatchesHandle(urlMapping, pageHandle)) return urlMapping;

      const forms = Array.from(document.querySelectorAll('form[action*="/cart/add"]'));
      for (const form of forms) {
        const mapping = mappingForForm(form);
        if (
          mapping
          && (
            handleForForm(form) === pageHandle
            || mappingMatchesHandle(mapping, pageHandle)
          )
        ) {
          return mapping;
        }
      }

      const defaultMapping = byVariant.get(defaultVariantByHandle.get(pageHandle));
      if (mappingMatchesHandle(defaultMapping, pageHandle)) return defaultMapping;
      return mappingForHandle(pageHandle, { requireUnique: true });
    };

    const isMappedProductPage = () => (
      Boolean(productPageHandle()) && Boolean(currentPageMapping())
    );

    const productPriceSummary = (mapping) => {
      if (!mapping) return '';
      const basePrice = mappingPrice(mapping);
      const shownPrice = displayCents(basePrice, mapping);

      if (isGrossInclusive(mapping)) {
        if (country === 'DE') {
          return text(
            `Shopify-Preis inkl. ${formatRate()}% MwSt.: ${formatMoney(shownPrice)} · für Privat- und Geschäftskunden`,
            `Shopify price incl. ${formatRate()}% VAT: ${formatMoney(shownPrice)} · for private and business customers`
          );
        }

        if (role === 'business' && Number.isFinite(vatRate())) {
          return text(
            `Voraussichtlicher Nettopreis nach gültiger USt-IdNr.: ${formatMoney(shownPrice)} · endgültig im Checkout`,
            `Estimated net price after a valid VAT ID: ${formatMoney(shownPrice)} · final at checkout`
          );
        }

        if (role === 'business') {
          return text(
            `Shopify-Preis: ${formatMoney(basePrice)} · Steuerabzug nach gültiger USt-IdNr. im Checkout`,
            `Shopify price: ${formatMoney(basePrice)} · tax deducted after a valid VAT ID at checkout`
          );
        }

        return text(
          `Shopify-Preis inkl. lokaler MwSt.: ${formatMoney(shownPrice)} · endgültig im Checkout`,
          `Shopify price incl. local VAT: ${formatMoney(shownPrice)} · final at checkout`
        );
      }

      if (country === 'DE' && role === 'private') {
        return text(
          `Preis mit 0% MwSt.: ${formatMoney(shownPrice)}`,
          `Price with 0% VAT: ${formatMoney(shownPrice)}`
        );
      }

      if (country === 'DE') {
        return text(
          `Preis inkl. ${formatRate()}% MwSt.: ${formatMoney(shownPrice)} · netto ${formatMoney(basePrice)}`,
          `Price incl. ${formatRate()}% VAT: ${formatMoney(shownPrice)} · net ${formatMoney(basePrice)}`
        );
      }

      if (role === 'private' && Number.isFinite(vatRate())) {
        return text(
          `Voraussichtlicher Preis inkl. ${formatRate()}% MwSt.: ${formatMoney(shownPrice)} · endgültig im Checkout`,
          `Estimated price incl. ${formatRate()}% VAT: ${formatMoney(shownPrice)} · final at checkout`
        );
      }

      return text(
        `Nettopreis: ${formatMoney(basePrice)} · Steuerbefreiung erst nach gültiger USt-IdNr.`,
        `Net price: ${formatMoney(basePrice)} · tax exemption only after a valid VAT ID`
      );
    };

    const legalTaxCopy = (mapping) => {
      if (isGrossInclusive(mapping)) {
        if (country === 'DE') {
          return text(
            'Preis inkl. 19 % MwSt.; der Shopify-Preis bleibt für Privat- und Geschäftskunden gleich.',
            'Price incl. 19% VAT; the Shopify price is the same for private and business customers.'
          );
        }

        if (role === 'business') {
          return text(
            'Steuerabzug erst nach erfolgreicher Prüfung einer gültigen USt-IdNr. im Checkout.',
            'Tax is deducted only after a valid VAT ID is successfully verified at checkout.'
          );
        }

        return text(
          'Shopify-Preis inkl. lokal geltender MwSt.; endgültige Steuer im Checkout.',
          'Shopify price incl. applicable local VAT; final tax at checkout.'
        );
      }

      if (country === 'DE' && role === 'private') {
        return text(
          '0 % MwSt. in Deutschland bei erfüllten Voraussetzungen · Versand- und Steuerdetails',
          '0% VAT in Germany if eligibility requirements are met · Shipping and tax details'
        );
      }

      if (country === 'DE') {
        return text(
          `${formatRate()} % MwSt. für Geschäftskunden in Deutschland · Versand- und Steuerdetails`,
          `${formatRate()}% VAT for business customers in Germany · Shipping and tax details`
        );
      }

      if (role === 'private') {
        return text(
          'Preis inkl. lokal geltender MwSt.; endgültige Steuer im Checkout.',
          'Price incl. applicable local VAT; final tax at checkout.'
        );
      }

      return text(
        'Nettopreis; Steuerbefreiung erst nach gültiger USt-IdNr. im Checkout.',
        'Net price; tax exemption only after a valid VAT ID at checkout.'
      );
    };

    const cleanLegalTaxBaseCopy = (value) => String(value || '')
      .replace(/\s*·\s*(?:Versand- und Steuerdetails|Shipping and tax details)\s*$/i, '')
      .trim();

    const renderLegalTaxCopy = () => {
      const mapping = currentPageMapping();
      if (!mapping) return;

      const candidates = Array.from(document.querySelectorAll(
        'main [data-mp-tax-legal-copy], main .ecom-g36majsklkn .text-content.ecom-html'
      ));

      candidates.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;

        const normalized = (node.textContent || '').replace(/\s+/g, ' ').trim();
        const isOriginalTaxCopy = (
          /^0\s*%\s*MwSt\.\s*in Deutschland\.\s*Kostenloser Versand\.\s*Zzgl\.\s*USt\.\s*nach anderer Steuerzone\.$/i.test(normalized)
          || /^0\s*%\s*VAT\s*in Germany\.\s*Free shipping\.\s*VAT may apply in another tax zone\.$/i.test(normalized)
        );
        if (!isOriginalTaxCopy && node.dataset.mpTaxLegalCopy !== 'true') return;

        const taxCopy = legalTaxCopy(mapping);
        const taxBaseCopy = cleanLegalTaxBaseCopy(taxCopy);
        const shippingCopy = String(node.dataset.mpShippingCopy || '').trim();
        const nextCopy = shippingCopy
          ? [taxBaseCopy, shippingCopy, text('Details', 'Details')].filter(Boolean).join(' · ')
          : taxCopy;
        node.dataset.mpTaxLegalCopy = 'true';
        node.dataset.mpTaxBaseCopy = taxBaseCopy;
        if (normalized !== nextCopy) node.textContent = nextCopy;
      });
    };

    const updateSelectorRoleButtons = () => {
      buttons.forEach((button) => {
        const active = button.dataset.mpTaxRole === role;
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    };

    const updateSelector = () => {
      updateSelectorRoleButtons();

      const currentMapping = currentPageMapping();
      const contextPrefix = isGrossInclusive(currentMapping) ? 'gross-' : '';
      let contextName;
      if (country === 'DE' && role === 'private') {
        contextName = `${contextPrefix}private-de`;
      } else if (country === 'DE') {
        contextName = `${contextPrefix}business-de`;
      } else if (role === 'business') {
        contextName = `${contextPrefix}business-foreign`;
      } else {
        contextName = `${contextPrefix}private-foreign`;
      }

      const template = contextTemplate(contextName);
      if (contextTitleNode) contextTitleNode.textContent = template?.dataset.title || '';
      if (contextNode) contextNode.textContent = normalizedTemplateText(template);
    };

    const setBusy = (busy) => {
      selector.classList.toggle('is-busy', busy);
      buttons.forEach((button) => {
        button.disabled = busy;
      });
    };

    const setStatus = (message, isError = false) => {
      if (!statusNode) return;
      statusNode.textContent = message || '';
      statusNode.classList.toggle('is-error', isError);
    };

    const fetchJson = (url, options = {}) => new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open(options.method || 'GET', url, true);
      request.withCredentials = true;
      request.setRequestHeader('Accept', options.headers?.Accept || 'application/json');
      if (options.body) request.setRequestHeader('Content-Type', 'application/json');
      Object.entries(options.headers || {}).forEach(([name, value]) => {
        if (name.toLowerCase() !== 'accept') request.setRequestHeader(name, value);
      });

      request.onload = () => {
        let payload = null;
        try {
          payload = request.responseText ? JSON.parse(request.responseText) : null;
        } catch (error) {
          payload = null;
        }

        if (request.status < 200 || request.status >= 300) {
          const message = payload && (payload.description || payload.message);
          reject(new Error(message || `Cart request failed (${request.status}).`));
          return;
        }

        resolve(payload);
      };
      request.onerror = () => reject(new Error('Cart request failed because of a network error.'));
      request.onabort = () => reject(new Error('Cart request was aborted.'));
      request.send(options.body || null);
    });

    const rememberCartItemCount = (cart) => {
      if (cart && Array.isArray(cart.items)) {
        knownCartItemCount = cart.items.reduce(
          (total, item) => total + Math.max(0, Number(item.quantity) || 0),
          0
        );
      }
      return cart;
    };

    const getCart = () => {
      reconcileDuplicateCartCookies();
      return fetchJson(route('cart.js')).then(rememberCartItemCount);
    };

    const postCart = (endpoint, body) => fetchJson(route(endpoint), {
      method: 'POST',
      body: JSON.stringify(body),
    });

    const sellingPlanId = (item) => {
      const allocation = item && item.selling_plan_allocation;
      const plan = allocation && allocation.selling_plan;
      return plan && plan.id ? plan.id : null;
    };

    const normalizedProperties = (item) => (
      item && item.properties && typeof item.properties === 'object' ? item.properties : {}
    );

    const propertiesSignature = (properties) => JSON.stringify(
      Object.keys(properties || {})
        .sort()
        .map((key) => [key, properties[key]])
    );

    const sameLogicalLine = (candidate, original) => (
      String(candidate.variant_id) === String(original.variant_id)
      && propertiesSignature(normalizedProperties(candidate)) === propertiesSignature(normalizedProperties(original))
      && String(sellingPlanId(candidate) || '') === String(sellingPlanId(original) || '')
    );

    const isUnsupportedBundleLine = (item) => {
      if (!item) return false;
      if (item.parent_relationship || item.item_components || item.components) return true;

      return Object.keys(normalizedProperties(item)).some((key) => (
        key.indexOf('_bundle_') === 0
        || key === '_parent_product'
        || key === '_component_reference'
      ));
    };

    const updateMappedCartClass = (cart) => {
      const hasMappedItem = Boolean(
        cart && Array.isArray(cart.items)
        && cart.items.some((item) => byVariant.has(String(item.variant_id)))
      );
      document.documentElement.classList.toggle('mp-tax-cart-mapped', hasMappedItem);
    };

    const variantInputForForm = (form) => {
      if (!(form instanceof HTMLFormElement)) return null;
      return form.querySelector('[name="id"]');
    };

    const handleForForm = (form) => {
      if (!(form instanceof HTMLFormElement)) return '';
      const explicitHandle = String(
        form.dataset.handle
        || form.getAttribute('data-handle')
        || form.getAttribute('product-handle')
        || ''
      ).toLowerCase();
      if (explicitHandle) return explicitHandle;

      const productLink = form.querySelector('a[href*="/products/"]')
        || form.closest('section')?.querySelector('a[href*="/products/"]');
      if (productLink instanceof HTMLAnchorElement) {
        try {
          const match = new URL(productLink.href, window.location.origin)
            .pathname
            .match(/(?:^|\/)products\/([^/]+)/);
          if (match) return decodeURIComponent(match[1]).toLowerCase();
        } catch (error) {
          return '';
        }
      }

      return productPageHandle();
    };

    const mappingForForm = (form) => {
      const variantInput = variantInputForForm(form);
      const variantId = normalizedId(variantInput?.value);
      if (variantId) {
        const currentMapping = byVariant.get(variantId);
        if (currentMapping) {
          form.dataset.mpTaxSourceVariantId = currentMapping.sourceVariantId;
          return currentMapping;
        }
        return null;
      }

      const handle = handleForForm(form);
      const handleMapping = mappingForHandle(handle, { requireUnique: true });
      if (handleMapping) {
        form.dataset.mpTaxSourceVariantId = handleMapping.sourceVariantId;
        return handleMapping;
      }

      return null;
    };

    const renderProductPrices = () => {
      const pageHandle = productPageHandle();
      if (!pageHandle) return;

      const mapping = currentPageMapping();
      if (!mapping) {
        ensureProductRules(pageHandle);
        return;
      }

      const netPrice = mappingPrice(mapping);
      const compareAtPrice = mappingCompareAtPrice(mapping);
      const pickup = document.querySelector('[data-mp-product-root] [data-mp-pickup-preview]');
      const pickupMapping = pickup && !pickup.hidden && pickup.dataset.selectedChoice === 'pickup'
        && pickup.dataset.priceVariantId === String(mapping.sourceVariantId)
        ? byVariant.get(pickup.dataset.cartVariantId) : null;
      const shownPrice = pickupMapping
        ? displayCents(mappingPrice(pickupMapping), pickupMapping)
        : displayCents(netPrice, mapping);
      const shownCompareAtPrice = displayCents(compareAtPrice, mapping);
      const hasCompareAtPrice = compareAtPrice > netPrice;
      const mainScope = document.querySelector('main') || document;
      const pageForms = Array.from(
        mainScope.querySelectorAll('form[action*="/cart/add"]')
      ).filter((form) => (
        mappingMatchesHandle(mappingForForm(form), pageHandle)
      ));

      pageForms.forEach((form) => {
        form.querySelectorAll('[data-product-price]').forEach((kallesPrice) => {
          if (kallesPrice.querySelector('[data-product-price]')) return;
          const saleNode = kallesPrice.querySelector('ins');
          const compareNode = kallesPrice.querySelector('del');
          writeMoneyText(saleNode || kallesPrice, shownPrice);
          if (compareNode) {
            compareNode.hidden = !hasCompareAtPrice;
            if (hasCompareAtPrice) writeMoneyText(compareNode, shownCompareAtPrice);
          }
          markRenderedCents(kallesPrice, 'mpTaxDisplayCents', shownPrice);
        });
      });

      mainScope.querySelectorAll('.ecom-product-single__price--sale').forEach((node) => {
        writeMoneyText(node, shownPrice);
        markRenderedCents(node, 'mpTaxDisplayCents', shownPrice);
      });

      mainScope.querySelectorAll('.ecom-product-single__price--regular').forEach((node) => {
        node.hidden = !hasCompareAtPrice;
        if (!hasCompareAtPrice) return;
        writeMoneyText(node, shownCompareAtPrice);
        markRenderedCents(node, 'mpTaxDisplayCents', shownCompareAtPrice);
      });

      mainScope.querySelectorAll('.ecom-els-aaq__product-price').forEach((node) => {
        writeMoneyText(node, shownPrice, true);
        markRenderedCents(node, 'mpTaxDisplayCents', shownPrice);
      });

      document.querySelectorAll('[data-sticky-addtocart]').forEach((stickyRoot) => {
        const stickyVariantId = normalizedId(
          stickyRoot
            .querySelector('[data-action-atc][data-variant-id]')
            ?.getAttribute('data-variant-id')
        );
        const stickyMapping = stickyVariantId
          ? byVariant.get(stickyVariantId)
          : currentPageMapping();
        const stickyPrice = stickyRoot.querySelector('[data-sticky-price]');
        if (!stickyMapping || !stickyPrice) return;

        const netPrice = mappingPrice(stickyMapping);
        const compareAtPrice = mappingCompareAtPrice(stickyMapping);
        const shownPrice = displayCents(netPrice, stickyMapping);
        const shownCompareAtPrice = displayCents(compareAtPrice, stickyMapping);
        const saleNode = stickyPrice.querySelector('ins');
        const compareNode = stickyPrice.querySelector('del');

        writeMoneyText(saleNode || stickyPrice, shownPrice);
        if (compareNode && compareAtPrice > netPrice) {
          writeMoneyText(compareNode, shownCompareAtPrice);
        }
        markRenderedCents(stickyPrice, 'mpTaxDisplayCents', shownPrice);
      });

      if (priceNode && isMappedProductPage()) {
        priceNode.textContent = productPriceSummary(mapping);
      }
      renderLegalTaxCopy();
    };

    const handleFromProductLink = (link) => {
      if (!(link instanceof HTMLAnchorElement)) return '';

      try {
        const path = new URL(link.href, window.location.origin).pathname;
        const match = path.match(/(?:^|\/)products\/([^/]+)/);
        return match ? decodeURIComponent(match[1]).toLowerCase() : '';
      } catch (error) {
        return '';
      }
    };

    const productCardSelector = [
      '.ecom-collection__product-item',
      '.t4s-product',
      '.t4s-product-item',
      '.t4s-widget__pr',
      '.t4s-minicart-recommendations__item',
      '.ecom-product-upsell__item',
      '.product-item',
      '.core__column--wrapper',
      'li.grid__item',
    ].join(', ');
    const productCardRoot = (node) => node instanceof Element
      ? node.closest(productCardSelector)
      : null;

    const waitForProductRuleRetry = (delay) => new Promise((resolve) => {
      window.setTimeout(resolve, delay);
    });

    const runProductRuleQueue = () => {
      while (
        activeProductRuleLoads < maxConcurrentProductRuleLoads
        && productRuleQueue.length > 0
      ) {
        const task = productRuleQueue.shift();
        activeProductRuleLoads += 1;
        task()
          .catch(() => {})
          .finally(() => {
            activeProductRuleLoads -= 1;
            runProductRuleQueue();
          });
      }
    };

    const queueProductRuleLoad = (task) => new Promise((resolve) => {
      productRuleQueue.push(async () => {
        try {
          resolve(await task());
        } catch (error) {
          resolve(null);
          throw error;
        }
      });
      runProductRuleQueue();
    });

    const ensureProductRules = (handle) => {
      const normalizedHandle = String(handle || '').trim().toLowerCase();
      if (!normalizedHandle) return Promise.resolve(null);
      const cachedPayload = readCachedProductRules(normalizedHandle);
      if (cachedPayload) {
        registerRulePayload(cachedPayload);
        rememberDefaultVariant(cachedPayload);
        return Promise.resolve(cachedPayload);
      }
      if (productRuleLoads.has(normalizedHandle)) {
        return productRuleLoads.get(normalizedHandle);
      }
      if ((productRuleRetryAfter.get(normalizedHandle) || 0) > Date.now()) {
        return Promise.resolve(null);
      }

      const request = queueProductRuleLoad(async () => {
        let finalError = null;
        for (let attempt = 0; attempt < 3; attempt += 1) {
          try {
            const payload = await fetchJson(
              `${route(`products/${encodeURIComponent(normalizedHandle)}`)}?view=mp-tax`,
              {
                headers: { Accept: 'text/html' },
              }
            );
            if (!validProductRulePayload(payload, normalizedHandle)) {
              throw new Error('Unexpected product display-rule payload');
            }
            registerRulePayload(payload);
            rememberDefaultVariant(payload);
            writeCachedProductRules(normalizedHandle, payload);
            productRuleRetryAfter.delete(normalizedHandle);
            scheduleVisualPriceRefresh();
            return payload;
          } catch (error) {
            finalError = error;
            if (attempt < 2) {
              await waitForProductRuleRetry(attempt === 0 ? 350 : 900);
            }
          }
        }

        productRuleLoads.delete(normalizedHandle);
        productRuleRetryAfter.set(normalizedHandle, Date.now() + 30000);
        console.warn(
          `[Mini Power Tax] Product display rules could not be loaded for ${normalizedHandle}.`,
          finalError
        );
        return null;
      });

      productRuleLoads.set(normalizedHandle, request);
      return request;
    };

    const handleForCard = (card) => {
      const localHandle = String(
        card?.dataset.mpTaxHandle || card?.dataset.handle || ''
      ).trim().toLowerCase();
      if (localHandle) return localHandle;

      const productOptions = card?.getAttribute('data-product-options');
      if (productOptions) {
        try {
          const parsed = JSON.parse(productOptions);
          if (parsed?.handle) return String(parsed.handle).trim().toLowerCase();
        } catch (error) {
          // Continue with the card's product link.
        }
      }

      return handleFromProductLink(card?.querySelector('a[href*="/products/"]'));
    };

    const taxProfileForCardVariant = (card, variantId) => {
      const node = card?.querySelector('[data-variant-data]');
      if (!node) return { known: false, mode: '' };

      try {
        const payload = JSON.parse(node.textContent || 'null');
        const variant = Array.isArray(payload?.variants)
          ? payload.variants.find((entry) => normalizedId(entry?.id) === variantId)
          : null;
        return variant
          ? { known: true, mode: String(variant.taxMode || '') }
          : { known: false, mode: '' };
      } catch (error) {
        return { known: false, mode: '' };
      }
    };

    const collectionBatchRootForCard = (card) => (
      card instanceof Element ? card.closest('[data-mp-tax-batch-url]') : null
    );

    const collectionBatchDescriptorForCard = (card) => {
      const root = collectionBatchRootForCard(card);
      if (!root) return null;
      const expectedCollectionHandle = String(
        root.dataset.mpTaxCollectionHandle || ''
      ).trim().toLowerCase();
      if (!expectedCollectionHandle) return null;
      const page = Math.max(
        1,
        Number.parseInt(card.dataset.mpTaxBatchPage || root.dataset.mpTaxBatchPage || '1', 10) || 1
      );
      const baseUrl = root.dataset.mpTaxBatchBaseUrl || root.dataset.mpTaxBatchUrl || '';
      try {
        const url = new URL(baseUrl, window.location.origin);
        Array.from(url.searchParams.keys()).forEach((key) => {
          if (key === 'sort_by' || key.startsWith('filter.')) url.searchParams.delete(key);
        });
        const currentUrl = new URL(window.location.href);
        const copiedKeys = new Set();
        currentUrl.searchParams.forEach((value, key) => {
          if (key !== 'sort_by' && !key.startsWith('filter.')) return;
          if (!copiedKeys.has(key)) {
            url.searchParams.delete(key);
            copiedKeys.add(key);
          }
          url.searchParams.append(key, value);
        });
        url.searchParams.set('view', 'mp-tax');
        url.searchParams.set('page', String(page));
        const batchUrl = normalizedCollectionBatchUrl(url.href);
        if (collectionHandleFromBatchUrl(batchUrl) !== expectedCollectionHandle) return null;
        if (batchUrl && Number(root.dataset.mpTaxBatchPage || 1) === page) {
          root.dataset.mpTaxBatchUrl = batchUrl;
        }
        return batchUrl ? { root, page, batchUrl, expectedCollectionHandle } : null;
      } catch (error) {
        return null;
      }
    };

    let documentResponseEndPromise = null;
    const waitForDocumentResponseEnd = () => {
      if (documentResponseEndPromise) return documentResponseEndPromise;
      documentResponseEndPromise = new Promise((resolve) => {
        const afterDomReady = () => {
          const navigation = window.performance?.getEntriesByType?.('navigation')?.[0];
          const remaining = Math.max(
            0,
            Math.ceil(Number(navigation?.responseEnd || 0) - Number(window.performance?.now?.() || 0))
          );
          window.setTimeout(resolve, remaining);
        };
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', afterDomReady, { once: true });
        } else {
          afterDomReady();
        }
      });
      return documentResponseEndPromise;
    };

    const cardRulesReady = (card) => {
      const variantId = normalizedId(
        card?.dataset.mpTaxVariantId
        || card?.querySelector('[data-mp-tax-default-variant-id]')?.getAttribute('data-mp-tax-default-variant-id')
      );
      if (!variantId) return false;
      const profile = taxProfileForCardVariant(card, variantId);
      if (!profile.known) return false;
      if (!profile.mode) return true;
      return (
        (profile.mode === 'de0_twin' || profile.mode === 'gross_inclusive_single')
        && !conflictedVariantIds.has(variantId)
        && byVariant.has(variantId)
      );
    };

    const isVisibleTaxCard = (card) => {
      if (!(card instanceof Element) || !card.isConnected) return false;
      const rect = card.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < window.innerHeight && rect.width > 0 && rect.height > 0;
    };

    const cardsForCollectionBatch = ({ root, page }) => (
      taxCardRootsWithin(root).filter((card) => (
        Math.max(1, Number.parseInt(card.dataset.mpTaxBatchPage || '1', 10) || 1) === page
      ))
    );

    const settleCollectionBatchDescriptor = (descriptor, payload) => {
      const { root, page } = descriptor;
      if (Number(root.dataset.mpTaxBatchPage || 1) === page) {
        root.dataset.mpTaxBatchPending = payload ? 'false' : 'failed';
      }
      const payloadHandles = new Set(
        payload?.products?.map((productPayload) => String(productPayload.handle || '').toLowerCase()) || []
      );
      cardsForCollectionBatch(descriptor).forEach((card) => {
        const handle = handleForCard(card);
        if (payload && payloadHandles.has(handle)) {
          if (cardRulesReady(card)) {
            card.dataset.mpTaxRulesPending = 'false';
            renderProductCardPrices(card);
          } else if (isVisibleTaxCard(card)) {
            loadProductRulesForCard(card, handle);
          }
          return;
        }
        if (isVisibleTaxCard(card)) loadProductRulesForCard(card, handle);
      });
    };

    const ensureCollectionRuleBatch = async (descriptor) => {
      if (!descriptor?.batchUrl) return null;
      const batchUrl = normalizedCollectionBatchUrl(descriptor.batchUrl);
      if (!batchUrl) return null;

      let request = collectionRuleBatchLoads.get(batchUrl);
      if (!request) {
        request = (async () => {
          await waitForDocumentResponseEnd();
          const cachedPayload = readCachedCollectionRuleBatch(
            batchUrl,
            descriptor.expectedCollectionHandle
          );
          if (cachedPayload) {
            registerCollectionRuleBatch(cachedPayload);
            return cachedPayload;
          }
          try {
            const payload = await fetchJson(batchUrl, {
              headers: { Accept: 'text/html' },
            });
            if (!validCollectionRuleBatchPayload(
              payload,
              batchUrl,
              descriptor.expectedCollectionHandle
            )) {
              throw new Error('Unexpected collection display-rule payload');
            }
            registerCollectionRuleBatch(payload);
            writeCachedCollectionRuleBatch(batchUrl, payload);
            return payload;
          } catch (error) {
            console.warn('[Mini Power Tax] Collection display rules could not be loaded.', error);
            return null;
          }
        })();
        collectionRuleBatchLoads.set(batchUrl, request);
      }

      const payload = await request;
      settleCollectionBatchDescriptor(descriptor, payload);
      return payload;
    };

    const ensureVariant = async (card, variantId) => {
      const normalizedVariantId = normalizedId(variantId);
      if (!(card instanceof Element) || !normalizedVariantId) return false;

      hydrateTaxRules(card);
      const taxProfile = taxProfileForCardVariant(card, normalizedVariantId);
      if (!taxProfile.known) return false;
      if (!taxProfile.mode) return true;
      if (taxProfile.mode !== 'de0_twin' && taxProfile.mode !== 'gross_inclusive_single') {
        return false;
      }
      if (conflictedVariantIds.has(normalizedVariantId)) return false;
      if (byVariant.has(normalizedVariantId)) return true;

      const handle = handleForCard(card);
      if (!handle) return false;
      const batchDescriptor = collectionBatchDescriptorForCard(card);
      if (batchDescriptor) {
        const batchPayload = await ensureCollectionRuleBatch(batchDescriptor);
        if (
          !conflictedVariantIds.has(normalizedVariantId)
          && byVariant.has(normalizedVariantId)
        ) return true;
      }

      const payload = await ensureProductRules(handle);
      return Boolean(
        payload
        && !conflictedVariantIds.has(normalizedVariantId)
        && byVariant.has(normalizedVariantId)
      );
    };

    window.MiniPowerTax = Object.assign(window.MiniPowerTax || {}, { ensureVariant });

    const taxCardRootsWithin = (scope = document) => {
      const cards = new Set(nodesWithin(scope, '[data-mp-tax-card]'));
      nodesWithin(
        scope,
        '[data-mp-tax-rule], script[type="application/json"][data-mp-tax-rules]'
      ).forEach((node) => {
        const card = productCardRoot(node);
        if (card) cards.add(card);
      });
      return Array.from(cards);
    };

    const pendingProductRuleCards = new WeakMap();
    const loadingProductRuleCards = new WeakMap();
    let productRuleIntersectionObserver = null;
    const loadProductRulesForCard = (card, handle) => {
      if (!card || !handle) return;
      if (loadingProductRuleCards.get(card) === handle) return;
      loadingProductRuleCards.set(card, handle);
      productRuleIntersectionObserver?.unobserve(card);
      pendingProductRuleCards.delete(card);
      ensureProductRules(handle).then((payload) => {
        if (payload && card.isConnected && cardRulesReady(card)) {
          card.dataset.mpTaxRulesPending = 'false';
          renderProductCardPrices(card);
        }
      }).finally(() => {
        loadingProductRuleCards.delete(card);
      });
    };

    if ('IntersectionObserver' in window) {
      productRuleIntersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const handle = pendingProductRuleCards.get(entry.target);
          loadProductRulesForCard(entry.target, handle);
        });
      }, { rootMargin: '400px 0px' });
    }

    const queueProductRulesForCard = (card, handle) => {
      if (!card || !handle || variantIdsByHandle.has(handle)) return;
      if (pendingProductRuleCards.get(card) === handle) return;
      pendingProductRuleCards.set(card, handle);
      if (productRuleIntersectionObserver) {
        productRuleIntersectionObserver.observe(card);
      } else {
        const rect = card.getBoundingClientRect();
        if (rect.bottom >= -400 && rect.top <= window.innerHeight + 400) {
          loadProductRulesForCard(card, handle);
        }
      }
    };

    const variantIdFromCartAddHref = (href) => {
      if (!href) return '';
      try {
        const url = new URL(href, window.location.origin);
        const queryId = normalizedId(url.searchParams.get('id'));
        if (queryId) return queryId;
        const pathMatch = url.pathname.match(/\/cart\/add\/(\d+)(?:\/|$)/);
        return normalizedId(pathMatch && pathMatch[1]);
      } catch (error) {
        return '';
      }
    };

    const exactVariantIdWithin = (root) => {
      if (!(root instanceof Element)) return '';

      const variantSelect = root.querySelector('select[name="ecom-product-upsell-select"]');
      if (variantSelect instanceof HTMLSelectElement) {
        const selectedId = normalizedId(variantSelect.value);
        if (selectedId) return selectedId;
      }

      const upsellVariantInput = root.querySelector('input[name="product_id"]');
      if (upsellVariantInput instanceof HTMLInputElement) {
        const inputId = normalizedId(upsellVariantInput.value);
        if (inputId) return inputId;
      }

      const activeVariantNode = root.querySelector(
        '[data-action-atc][data-variant-id], .ecom-ajax-cart-simple[data-id], '
        + 'a[href*="/cart/add"]'
      );
      if (activeVariantNode) {
        const activeVariantId = normalizedId(
          activeVariantNode.getAttribute('data-variant-id')
          || activeVariantNode.getAttribute('data-id')
        ) || variantIdFromCartAddHref(activeVariantNode.getAttribute('href'));
        if (activeVariantId) return activeVariantId;
      }

      const variantProductLink = root.querySelector(
        'a[href*="/products/"][href*="variant="]'
      );
      if (variantProductLink instanceof HTMLAnchorElement) {
        try {
          const linkVariantId = normalizedId(
            new URL(variantProductLink.href, window.location.origin)
              .searchParams
              .get('variant')
          );
          if (linkVariantId) return linkVariantId;
        } catch (error) {
          // Continue with the fail-closed embedded rule identifiers below.
        }
      }

      const ownId = normalizedId(root.getAttribute('data-mp-tax-variant-id'));
      if (ownId) return ownId;

      const defaultNode = root.querySelector('[data-mp-tax-default-variant-id]');
      const defaultId = normalizedId(
        defaultNode?.getAttribute('data-mp-tax-default-variant-id')
      );
      if (defaultId) return defaultId;

      const ruleNode = root.querySelector('[data-mp-tax-variant-id]');
      return normalizedId(ruleNode?.getAttribute('data-mp-tax-variant-id'));
    };

    const mappingForCard = (card, handle = '', { preferLowest = false } = {}) => {
      if (preferLowest) {
        const lowestMapping = lowestProductMappingForHandle(handle);
        if (lowestMapping) return lowestMapping;
      }
      const exactVariantId = exactVariantIdWithin(card);
      if (exactVariantId) return byVariant.get(exactVariantId) || null;
      const defaultVariantId = defaultVariantByHandle.get(String(handle || '').toLowerCase());
      if (defaultVariantId) return byVariant.get(defaultVariantId) || null;
      return mappingForHandle(handle, { requireUnique: true });
    };

    const updateKallesCardPrice = (
      card,
      shownPrice,
      shownCompareAtPrice,
      hasCompareAtPrice,
      shownMaximumPrice = shownPrice
    ) => {
      const priceNodes = Array.from(card.querySelectorAll('[data-product-price]')).filter((node) => (
        !node.querySelector('[data-product-price]')
      ));

      priceNodes.forEach((node) => {
        const saleNode = node.querySelector('ins');
        const compareNode = node.querySelector('del');

        if (saleNode) {
          const fromNode = saleNode.querySelector('.t4s-price-from');
          if (fromNode) {
            writeMoneyText(fromNode, shownPrice);
          } else {
            writeMoneyText(saleNode, shownPrice);
          }
        } else if (!node.querySelector('.t4s-price__unit')) {
          const rangeNode = node.querySelector('.t4s-price__sale');
          const fromNode = node.querySelector('.t4s-price-from');
          if (shownMaximumPrice > shownPrice && rangeNode) {
            const nextRange = `${formatMoney(shownPrice)} – ${formatMoney(shownMaximumPrice)}`;
            if ((rangeNode.textContent || '').trim() !== nextRange) {
              rangeNode.textContent = nextRange;
            }
          } else if (shownMaximumPrice > shownPrice && (node.textContent || '').includes('–')) {
            const nextRange = `${formatMoney(shownPrice)} – ${formatMoney(shownMaximumPrice)}`;
            if ((node.textContent || '').trim() !== nextRange) node.textContent = nextRange;
          } else if (fromNode) {
            writeMoneyText(fromNode, shownPrice);
          } else {
            writeMoneyText(node, shownPrice);
          }
        }

        if (compareNode && hasCompareAtPrice) {
          writeMoneyText(compareNode, shownCompareAtPrice);
        }

        markRenderedCents(node, 'mpTaxCardCents', shownPrice);
      });
    };

    const updateEcomposerCardPrice = (card, shownPrice, shownCompareAtPrice, hasCompareAtPrice) => {
      card.querySelectorAll(
        '.ecom-collection__product-price--sale, '
        + '.ecom-collection__product-price:not(.ecom-collection__product--compare-at-price), '
        + '.ecom-product-upsell__price--sale'
      ).forEach((node) => {
        writeMoneyText(node, shownPrice);
        markRenderedCents(node, 'mpTaxCardCents', shownPrice);
      });

      if (hasCompareAtPrice) {
        card.querySelectorAll(
          '.ecom-collection__product--compare-at-price, '
          + '.ecom-collection__product-price--regular, '
          + '.ecom-product-upsell__price--regular'
        ).forEach((node) => {
          writeMoneyText(node, shownCompareAtPrice);
          markRenderedCents(node, 'mpTaxCardCents', shownCompareAtPrice);
        });
      }

    };

    const prepareCollectionPriceState = (scope = document) => {
      taxCardRootsWithin(scope).forEach((card) => {
        if (!collectionBatchDescriptorForCard(card)) return;
        const handle = handleForCard(card);
        if (completeRulePayloadHandles.has(handle)) {
          card.dataset.mpTaxRulesPending = cardRulesReady(card) ? 'false' : 'true';
          return;
        }
        const variantId = normalizedId(card.dataset.mpTaxVariantId);
        const profile = taxProfileForCardVariant(card, variantId);
        const needsRules = card.getAttribute('data-mp-tax-needs-rules') === 'true';
        card.dataset.mpTaxRulesPending = (
          profile.known && !profile.mode && !needsRules
        ) ? 'false' : 'true';
      });
    };

    const renderProductCardPrices = (scope = document) => {
      taxCardRootsWithin(scope).forEach((card) => {
        const handle = handleForCard(card);
        if (completeRulePayloadHandles.has(handle) && cardRulesReady(card)) {
          card.dataset.mpTaxRulesPending = 'false';
        }
        if (card.dataset.mpTaxRulesPending === 'true') return;
        if (
          collectionBatchDescriptorForCard(card)
          && !completeRulePayloadHandles.has(handle)
        ) return;
        const hasVariablePrice = Boolean(
          card.getAttribute('data-mp-tax-price-varies') === 'true'
          ||
          card.querySelector('[data-mp-tax-price-varies="true"]')
        );
        const needsProductRules = hasVariablePrice || Boolean(
          card.getAttribute('data-mp-tax-needs-rules') === 'true'
          || card.querySelector('[data-mp-tax-needs-rules="true"]')
        );
        if (
          needsProductRules
          && !variantIdsByHandle.has(handle)
          && !selectedRuleSnapshotHandles.has(handle)
          && !completeRulePayloadHandles.has(handle)
        ) {
          queueProductRulesForCard(card, handle);
          return;
        }
        const mapping = mappingForCard(card, handle, { preferLowest: hasVariablePrice });
        if (!mapping) return;

        const netPrice = mappingPrice(mapping);
        const compareAtPrice = mappingCompareAtPrice(mapping);
        const displayedRange = hasVariablePrice ? displayedProductPriceRange(handle) : null;
        const shownPrice = displayedRange?.min || displayCents(netPrice, mapping);
        const shownCompareAtPrice = displayCents(compareAtPrice, mapping);
        const hasCompareAtPrice = compareAtPrice > netPrice;

        updateKallesCardPrice(
          card,
          shownPrice,
          shownCompareAtPrice,
          hasCompareAtPrice,
          displayedRange?.max || shownPrice
        );
        updateEcomposerCardPrice(card, shownPrice, shownCompareAtPrice, hasCompareAtPrice);
        card.dataset.mpTaxCardRole = role;
      });
    };

    const preloadVariableCardRules = (target) => {
      const card = productCardRoot(target);
      if (!card) return;
      const isTaxCard = card.matches('[data-mp-tax-card]') || Boolean(
        card.querySelector(
          '[data-mp-tax-rule], script[type="application/json"][data-mp-tax-rules]'
        )
      );
      const hasVariablePrice = card.getAttribute('data-mp-tax-price-varies') === 'true'
        || Boolean(card.querySelector('[data-mp-tax-price-varies="true"]'));
      const needsProductRules = hasVariablePrice
        || card.getAttribute('data-mp-tax-needs-rules') === 'true'
        || Boolean(card.querySelector('[data-mp-tax-needs-rules="true"]'));
      if (!isTaxCard) return;
      const batchDescriptor = collectionBatchDescriptorForCard(card);
      if (
        batchDescriptor
        && !completeRulePayloadHandles.has(handleForCard(card))
      ) {
        if (batchDescriptor) ensureCollectionRuleBatch(batchDescriptor).catch(() => {});
        return;
      }
      if (!needsProductRules) return;
      const handle = handleForCard(card);
      if (
        !handle
        || completeRulePayloadHandles.has(handle)
      ) return;
      loadProductRulesForCard(card, handle);
    };

    const collectionBatchDescriptorsWithin = (scope = document) => {
      const descriptors = new Map();
      nodesWithin(scope, '[data-mp-tax-batch-url]').forEach((root) => {
        if (root.dataset.mpTaxBatchPending !== 'true') return;
        const page = Math.max(1, Number.parseInt(root.dataset.mpTaxBatchPage || '1', 10) || 1);
        const card = root.querySelector(
          `[data-mp-tax-card][data-mp-tax-batch-page="${page}"]`
        );
        const descriptor = card ? collectionBatchDescriptorForCard(card) : null;
        if (descriptor) descriptors.set(descriptor.batchUrl, descriptor);
      });
      taxCardRootsWithin(scope).forEach((card) => {
        if (card.dataset.mpTaxRulesPending !== 'true') return;
        const descriptor = collectionBatchDescriptorForCard(card);
        if (descriptor) descriptors.set(descriptor.batchUrl, descriptor);
      });
      return Array.from(descriptors.values());
    };

    const scheduleCollectionRuleBatches = (scope = document) => {
      const descriptors = collectionBatchDescriptorsWithin(scope);
      if (!descriptors.length) return;
      waitForDocumentResponseEnd().then(() => {
        window.requestAnimationFrame(() => {
          window.setTimeout(() => {
            descriptors.forEach((descriptor) => {
              ensureCollectionRuleBatch(descriptor).catch(() => {});
            });
          }, 0);
        });
      });
    };

    const handleVariableCardPointerover = (event) => {
      if (event.target instanceof Element) preloadVariableCardRules(event.target);
    };
    const handleVariableCardFocusin = (event) => {
      if (event.target instanceof Element) preloadVariableCardRules(event.target);
    };

    const kallesSwatchSelector = '.t4s-pr-color__item:not(.is-swatch--current):not([data-img="none"]):not(.is--colors-more)';
    const kallesSwatchRefreshTimers = new WeakMap();
    const scheduleKallesSwatchRefresh = (target) => {
      const swatch = target instanceof Element ? target.closest(kallesSwatchSelector) : null;
      const card = productCardRoot(swatch);
      if (!card) return;
      const previousTimer = kallesSwatchRefreshTimers.get(card);
      if (previousTimer) window.clearTimeout(previousTimer);
      const timer = window.setTimeout(() => {
        kallesSwatchRefreshTimers.delete(card);
        processTaxContent(card);
      }, 140);
      kallesSwatchRefreshTimers.set(card, timer);
    };
    const handleKallesSwatchPointerover = (event) => scheduleKallesSwatchRefresh(event.target);
    const handleKallesSwatchClick = (event) => scheduleKallesSwatchRefresh(event.target);

    let scheduledVisualPriceRefresh = null;
    const scheduleVisualPriceRefresh = () => {
      if (scheduledVisualPriceRefresh) return;
      scheduledVisualPriceRefresh = window.setTimeout(() => {
        scheduledVisualPriceRefresh = null;
        renderProductPrices();
        renderProductCardPrices();
        if (latestCart && cartTaxProfile(latestCart).mappedCount > 0) renderCartPrices(latestCart);
      }, 80);
    };

    const findCartLine = (variantId) => {
      const variant = String(variantId);
      return Array.from(document.querySelectorAll('[data-cart-item]')).find((line) => (
        line.getAttribute('data-mp-tax-variant-id') === variant
        || line.classList.contains(`cart_item_${variant}`)
        || Boolean(line.querySelector(`a[href*="variant=${variant}"]`))
      ));
    };

    const cartTaxProfile = (cart) => {
      const profile = {
        hasGross: false,
        hasTwin: false,
        hasUnmapped: false,
        mappedCount: 0,
      };

      (cart?.items || []).forEach((item) => {
        const mapping = byVariant.get(String(item.variant_id));
        if (!mapping) {
          profile.hasUnmapped = true;
          return;
        }

        profile.mappedCount += 1;
        if (isGrossInclusive(mapping)) {
          profile.hasGross = true;
        } else {
          profile.hasTwin = true;
        }
      });

      return profile;
    };

    const cartPriceSummary = (cart, displayedTotal) => {
      if (!cart || !Array.isArray(cart.items)) return '';
      const profile = cartTaxProfile(cart);
      if (profile.mappedCount === 0) return '';

      if (country === 'DE' && role === 'private') {
        if (profile.hasUnmapped || (profile.hasGross && profile.hasTwin)) {
          return text(
            `Zwischensumme: ${formatMoney(displayedTotal)} · enthält Positionen mit unterschiedlicher Steuerbehandlung`,
            `Subtotal: ${formatMoney(displayedTotal)} · contains items with different tax treatment`
          );
        }

        if (profile.hasGross) {
          return text(
            `Zwischensumme inkl. ${formatRate()}% MwSt.: ${formatMoney(displayedTotal)}`,
            `Subtotal incl. ${formatRate()}% VAT: ${formatMoney(displayedTotal)}`
          );
        }

        return text(
          `Warenkorb mit 0% MwSt.: ${formatMoney(displayedTotal)}`,
          `Cart with 0% VAT: ${formatMoney(displayedTotal)}`
        );
      }

      if (country === 'DE') {
        return text(
          `Zwischensumme inkl. ${formatRate()}% MwSt.: ${formatMoney(displayedTotal)} · endgültige Steuer im Checkout`,
          `Subtotal incl. ${formatRate()}% VAT: ${formatMoney(displayedTotal)} · final tax at checkout`
        );
      }

      if (role === 'private' && Number.isFinite(vatRate())) {
        return text(
          `Voraussichtliche Zwischensumme inkl. ${formatRate()}% MwSt.: ${formatMoney(displayedTotal)} · endgültig im Checkout`,
          `Estimated subtotal incl. ${formatRate()}% VAT: ${formatMoney(displayedTotal)} · final at checkout`
        );
      }

      if (role === 'business' && Number.isFinite(vatRate()) && !profile.hasUnmapped) {
        return text(
          `Voraussichtliche Nettosumme nach gültiger USt-IdNr.: ${formatMoney(displayedTotal)} · endgültig im Checkout`,
          `Estimated net subtotal after a valid VAT ID: ${formatMoney(displayedTotal)} · final at checkout`
        );
      }

      return text(
        `Voraussichtliche Zwischensumme: ${formatMoney(displayedTotal)} · endgültige Steuer im Checkout`,
        `Estimated subtotal: ${formatMoney(displayedTotal)} · final tax at checkout`
      );
    };

    const renderCartPrices = (cart) => {
      if (!cart || !Array.isArray(cart.items)) return;
      latestCart = cart;

      let displayedTotal = 0;

      cart.items.forEach((item) => {
        const mapping = byVariant.get(String(item.variant_id));
        const netUnitPrice = Number(item.final_price ?? item.price) || 0;
        const netOriginalPrice = Number(item.original_price ?? item.price) || netUnitPrice;
        const netCompareAtPrice = mapping
          ? mappingCompareAtPrice(mapping)
          : netOriginalPrice;
        const netLinePrice = Number(item.final_line_price ?? item.line_price) || 0;
        const shownUnitPrice = mapping ? displayCents(netUnitPrice, mapping) : netUnitPrice;
        const shownCompareAtPrice = mapping
          ? displayCents(netCompareAtPrice, mapping)
          : netOriginalPrice;
        const shownLinePrice = mapping ? displayCents(netLinePrice, mapping) : netLinePrice;

        displayedTotal += shownLinePrice;

        if (!mapping) return;
        const line = findCartLine(item.variant_id);
        if (!line) return;

        line.querySelectorAll('[data-mp-tax-unit-price]').forEach((node) => {
          node.textContent = formatMoney(shownUnitPrice);
        });
        line.querySelectorAll('[data-mp-tax-compare-price]').forEach((node) => {
          node.hidden = !(netCompareAtPrice > netUnitPrice);
          if (!node.hidden) node.textContent = formatMoney(shownCompareAtPrice);
        });
        line.querySelectorAll('[data-mp-tax-line-price]').forEach((node) => {
          node.textContent = formatMoney(shownLinePrice);
        });
      });

      document.querySelectorAll('[data-cart-tt-price]').forEach((node) => {
        node.textContent = formatMoney(displayedTotal);
      });

      document.querySelectorAll('.t4s-cart__totalPrice, .ecom-ajax-cart-totals__subtotal-value').forEach((node) => {
        node.textContent = formatMoney(displayedTotal, true);
      });

      const displayedSaving = Math.floor(displayedTotal * 3 / 100);
      document.querySelectorAll('[data-mp-vorkasse-saving]').forEach((node) => {
        node.textContent = '− ' + formatMoney(displayedSaving);
      });

      if (priceNode && !isMappedProductPage()) {
        priceNode.textContent = cartPriceSummary(cart, displayedTotal);
      }
    };

    const applyDynamicCheckoutGuards = (scope = document) => {
      const forms = scope instanceof HTMLFormElement
        ? [scope]
        : Array.from(scope.querySelectorAll ? scope.querySelectorAll('form[action*="/cart/add"]') : []);

      forms.forEach((form) => {
        form.classList.toggle('mp-tax-mapped-form', Boolean(mappingForForm(form)));
      });
    };

    const mappedAddItem = (form, mapping) => {
      const quantityInput = form.querySelector('[name="quantity"]');
      const quantity = Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1);
      const item = {
        id: Number(desiredVariantId(mapping)),
        quantity,
      };
      const properties = {};

      form.querySelectorAll('[name^="properties["]').forEach((field) => {
        if (field.disabled) return;
        if (
          (field.type === 'checkbox' || field.type === 'radio')
          && !field.checked
        ) return;

        const match = field.name.match(/^properties\[(.*)\]$/);
        if (!match || !match[1]) return;
        properties[match[1]] = field.value;
      });

      if (Object.keys(properties).length > 0) item.properties = properties;

      const sellingPlanInput = form.querySelector('[name="selling_plan"]');
      if (sellingPlanInput && sellingPlanInput.value) {
        item.selling_plan = Number(sellingPlanInput.value);
      }

      return item;
    };

    const beginMappedItemAdd = (event, item) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (mappedAddPromise) return true;

      setBusy(true);
      setStatus(text('Artikel wird hinzugefügt …', 'Adding item …'));

      mappedAddPromise = postCart('cart/add.js', { items: Array.isArray(item) ? item : [item] })
        .then(() => performSync({ openDrawer: false, announce: false }))
        .then(() => {
          dispatchCartRefresh(true);
          setStatus(text('Artikel wurde zum Warenkorb hinzugefügt.', 'Item added to cart.'));
        })
        .catch((error) => {
          console.error('[Mini Power Tax] Mapped add-to-cart failed.', error);
          setStatus(
            text(
              'Der Artikel konnte nicht hinzugefügt werden. Bitte laden Sie die Seite neu und versuchen Sie es erneut.',
              'The item could not be added. Please reload the page and try again.'
            ),
            true
          );
        })
        .finally(() => {
          mappedAddPromise = null;
          setBusy(false);
        });

      return true;
    };

    const buildProductCartItems = (form) => {
      const mapping = mappingForForm(form);
      if (!mapping) throw new Error('Die Produktauswahl wird geladen. Bitte versuche es erneut.');
      const item = mappedAddItem(form, mapping);
      const items = form.mpSetBuildCartItems ? form.mpSetBuildCartItems(item) : [item];
      return form.mpAccessoryCartItems ? form.mpAccessoryCartItems(items) : items;
    };

    const beginMappedAdd = (event, form) => {
      if (!mappingForForm(form)) return false;
      try {
        return beginMappedItemAdd(event, buildProductCartItems(form));
      } catch (error) {
        event.preventDefault();
        event.stopImmediatePropagation();
        setStatus(error.message, true);
        return true;
      }
    };

    const beginMappedDirectAdd = (event, control) => {
      if (!(control instanceof Element)) return false;

      const variantId = normalizedId(
        control.getAttribute('data-mp-tax-variant-id')
        || control.getAttribute('data-variant-id')
        || control.getAttribute('data-id')
      ) || variantIdFromCartAddHref(control.getAttribute('href'));
      const mapping = byVariant.get(variantId);
      if (!mapping) return false;

      const root = control.closest(
        '.ecom-collection__product-item, .t4s-product, .t4s-product-item, '
        + '.product-item, .core__column--wrapper, li.grid__item'
      );
      const quantityInput = root && root.querySelector(
        '[data-quantity-value], input[name="quantity"]'
      );
      const quantity = Math.max(
        1,
        Number.parseInt(quantityInput?.value || '1', 10) || 1
      );

      return beginMappedItemAdd(event, {
        id: Number(desiredVariantId(mapping)),
        quantity,
      });
    };

    const cartAttributesMatch = (cart, attributes) => {
      const current = (cart && cart.attributes) || {};
      return Object.keys(attributes).every((key) => String(current[key] || '') === String(attributes[key]));
    };

    const cartAttributesForProfile = (taxProfile) => {
      const taxLogic = taxProfile.hasGross && taxProfile.hasTwin
        ? 'MIXED'
        : (taxProfile.hasGross ? 'GROSS_SINGLE' : (taxProfile.hasTwin ? 'DE0_TWIN' : 'UNMAPPED'));
      const displayMode = country === 'DE'
        ? (
          role === 'private'
            ? (
              taxProfile.hasGross && taxProfile.hasTwin
                ? 'Gemischte 0%/19%-Preise'
                : (taxProfile.hasGross ? 'Brutto 19%' : '0% Preis')
            )
            : 'Brutto 19%'
        )
        : (
          role === 'private'
            ? (
              Number.isFinite(vatRate())
                ? `Voraussichtliches Brutto ${formatRate()}%`
                : 'Voraussichtliches Brutto nach Lieferland'
            )
            : 'Voraussichtliches Netto nach gültiger USt-IdNr.'
        );

      return {
        'MP Kundentyp': role === 'private' ? 'Privatkunde' : 'Geschäftskunde',
        'MP Steuerlogik': taxLogic,
        'MP Länderkontext': country,
        'MP Deutschland Steuerwahl': country === 'DE'
          ? (
            role === 'private' && taxProfile.hasGross
              ? 'Privatkunde: produktabhängig 0%/19%'
              : (role === 'private' ? 'Privatkunde 0%' : 'Geschäftskunde 19%')
          )
          : 'Nicht anwendbar',
        'MP Preisanzeige': displayMode,
        'MP Ungemappte Positionen': taxProfile.hasUnmapped ? 'JA' : 'NEIN',
        'MP Steuerregel Version': 'DE-ROLE-TAX-v3-2026-07-29',
        'MP Preisdisplay Version': 'VAT-DISPLAY-v2-2026-07-29',
        'MP PV-Nullsteuersatz Erklärung': '',
        'MP PV-Erklärung Version': '',
      };
    };

    const dispatchCartRefresh = (openDrawer) => {
      ignoreRefreshEventsUntil = Date.now() + 800;
      document.dispatchEvent(new CustomEvent(openDrawer ? 'cart:refresh:opend' : 'cart:refresh'));
    };

    const performSync = async ({ openDrawer = false, announce = false, checkout = false } = {}) => {
      setBusy(true);
      setStatus(announce ? text('Warenkorb wird aktualisiert …', 'Updating cart …') : '');

      let cart = await getCart();
      let changed = false;
      let swapCount = 0;
      latestCart = cart;

      if (!cart.items.some((item) => byVariant.has(String(item.variant_id)))) {
        if (checkout && cart.items.length > 0) {
          throw new Error(text(
            'Mindestens eine Warenkorbposition ist noch nicht für die Steuerlogik freigegeben. Bitte kontaktieren Sie uns oder entfernen Sie die Position, bevor Sie zur Kasse gehen.',
            'At least one cart item is not yet approved for the tax logic. Please contact us or remove the item before checkout.'
          ));
        }
        updateMappedCartClass(cart);
        renderProductPrices();
        renderProductCardPrices();
        renderCartPrices(cart);
        setStatus(announce
          ? text('Auswahl wurde aktualisiert.', 'Selection updated.')
          : ''
        );
        return { cart, changed };
      }

      while (true) {
        const incorrectLine = cart.items.find((item) => {
          const mapping = byVariant.get(String(item.variant_id));
          return mapping && String(item.variant_id) !== desiredVariantId(mapping);
        });

        if (!incorrectLine) break;

        swapCount += 1;
        if (swapCount > 50) {
          throw new Error(text(
            'Die Warenkorb-Synchronisierung wurde aus Sicherheitsgründen abgebrochen.',
            'Cart synchronization was stopped for safety.'
          ));
        }

        if (isUnsupportedBundleLine(incorrectLine)) {
          throw new Error(text(
            `SKU ${incorrectLine.sku || ''} ist Teil eines nicht sicher übertragbaren Bundles.`,
            `SKU ${incorrectLine.sku || ''} belongs to a bundle that cannot be transferred safely.`
          ));
        }

        const mapping = byVariant.get(String(incorrectLine.variant_id));
        const targetVariantId = desiredVariantId(mapping);
        const addItem = {
          id: Number(targetVariantId),
          quantity: Number(incorrectLine.quantity),
        };
        const properties = normalizedProperties(incorrectLine);
        const planId = sellingPlanId(incorrectLine);

        if (Object.keys(properties).length > 0) addItem.properties = properties;
        if (planId) addItem.selling_plan = planId;

        await postCart('cart/add.js', { items: [addItem] });

        const afterAdd = await getCart();
        const originalLine = afterAdd.items.find((item) => item.key === incorrectLine.key)
          || afterAdd.items.find((item) => sameLogicalLine(item, incorrectLine));

        if (originalLine) {
          await postCart('cart/change.js', {
            id: originalLine.key,
            quantity: 0,
          });
        }

        cart = await getCart();
        changed = true;
      }

      const taxProfile = cartTaxProfile(cart);
      const attributes = cartAttributesForProfile(taxProfile);

      if (!cartAttributesMatch(cart, attributes)) {
        cart = await postCart('cart/update.js', { attributes });
        changed = true;
      }

      const invalidLine = cart.items.find((item) => {
        const mapping = byVariant.get(String(item.variant_id));
        return mapping && String(item.variant_id) !== desiredVariantId(mapping);
      });

      if (invalidLine) {
        throw new Error(text(
          'Der Warenkorb konnte nicht vollständig auf den gewählten Kundentyp umgestellt werden.',
          'The cart could not be fully synchronized to the selected customer type.'
        ));
      }

      if (checkout && taxProfile.hasUnmapped) {
        throw new Error(text(
          'Mindestens eine Warenkorbposition ist noch nicht für die Steuerlogik freigegeben. Bitte kontaktieren Sie uns oder entfernen Sie die Position, bevor Sie zur Kasse gehen.',
          'At least one cart item is not yet approved for the tax logic. Please contact us or remove the item before checkout.'
        ));
      }

      updateMappedCartClass(cart);
      renderProductPrices();
      renderProductCardPrices();
      renderCartPrices(cart);

      if (changed) {
        dispatchCartRefresh(openDrawer);
        [160, 600, 1400].forEach((delay) => {
          window.setTimeout(scheduleVisualPriceRefresh, delay);
        });
      }

      setStatus(announce
        ? text('Warenkorb und Steuerart wurden aktualisiert.', 'Cart and tax mode were updated.')
        : ''
      );

      return { cart, changed };
    };

    const syncCart = (options = {}) => {
      if (syncPromise) return syncPromise;

      syncPromise = performSync(options)
        .catch((error) => {
          console.error('[Mini Power Tax] Cart synchronization failed.', error);
          if (options.announce) {
            if (options.checkout) {
              setStatus(
                error instanceof Error && error.message
                  ? error.message
                  : text(
                    'Der Checkout wurde aus Sicherheitsgründen angehalten.',
                    'Checkout was stopped for safety.'
                  ),
                true
              );
              throw error;
            }
            const onCartPage = /\/cart(?:\/|$)/.test(window.location.pathname);
            if (onCartPage) {
              setStatus(
                text(
                  'Der Warenkorb konnte nicht aktualisiert werden. Bitte laden Sie die Seite neu und versuchen Sie es erneut.',
                  'The cart could not be updated. Please reload the page and try again.'
                ),
                true
              );
            } else {
              setStatus(
                text(
                  'Der sichtbare Preis wurde aktualisiert. Der Warenkorb wird beim Öffnen erneut synchronisiert.',
                  'The visible price was updated. The cart will be synchronized again when opened.'
                )
              );
            }
          }
          throw error;
        })
        .finally(() => {
          setBusy(false);
          syncPromise = null;
        });

      return syncPromise;
    };

    const scheduleSync = (openDrawer = false) => {
      if (scheduledSync) window.clearTimeout(scheduledSync);
      scheduledSync = window.setTimeout(() => {
        scheduledSync = null;
        syncCart({ openDrawer, announce: false }).catch(() => {});
      }, 180);
    };

    let deferredRuntimeListenersBound = false;
    const bindDeferredRuntimeListeners = () => {
      if (deferredRuntimeListenersBound) return;
      deferredRuntimeListenersBound = true;

      document.addEventListener('pointerover', handleVariableCardPointerover, { passive: true });
      document.addEventListener('focusin', handleVariableCardFocusin);
      document.addEventListener('pointerover', handleKallesSwatchPointerover, { passive: true });
      document.addEventListener('click', handleKallesSwatchClick);

    if (trustedLink) {
      trustedLink.addEventListener('click', (event) => {
        toggleDetails('trusted', trustedLink, { focusPanel: event.detail === 0 });
      });
    }

    if (bankDiscountButton) {
      bankDiscountButton.addEventListener('click', (event) => {
        toggleDetails('bank', bankDiscountButton, { focusPanel: event.detail === 0 });
      });
    }

    const applyCustomerRole = (nextRole, options = {}) => {
      if (nextRole !== 'private' && nextRole !== 'business') {
        return Promise.reject(new Error(text('Ungültiger Kundentyp.', 'Invalid customer type.')));
      }

      role = nextRole;
      try {
        window.localStorage.setItem(storageKey, role);
      } catch (error) {
        console.warn('[Mini Power Tax] Customer type could not be stored.', error);
      }

      prepareCollectionPriceState();
      updateSelector();
      if (options.trigger) {
        toggleDetails('role', options.trigger, { focusPanel: options.focusPanel === true });
      }
      renderProductPrices();
      renderProductCardPrices();
      scheduleCollectionRuleBatches();
      document.dispatchEvent(new CustomEvent('mp:tax-role-changed', {
        detail: { role }
      }));

      if (knownCartItemCount > 0) {
        return syncCart({ openDrawer: false, announce: true });
      }

      setStatus(text('Auswahl wurde aktualisiert.', 'Selection updated.'));
      return Promise.resolve({ changed: false });
    };

    window.MiniPowerTax = Object.assign(window.MiniPowerTax || {}, {
      buildProductCartItems,
      getRole: () => role,
      getMappedVariantId: (variantId) => {
        const mapping = byVariant.get(String(variantId));
        return mapping ? desiredVariantId(mapping) : null;
      },
      getVariantDisplayCents: (variantId, fallbackCents) => {
        const mapping = byVariant.get(String(variantId));
        return mapping ? displayCents(mappingPrice(mapping), mapping) : fallbackCents;
      },
      setRole: (nextRole) => applyCustomerRole(nextRole)
    });

    buttons.forEach((button) => {
      button.addEventListener('click', (event) => {
        applyCustomerRole(button.dataset.mpTaxRole, {
          trigger: button,
          focusPanel: event.detail === 0
        }).catch(() => {});
      });
    });

    if (countrySelect) {
      countrySelect.addEventListener('change', () => {
        closeDetails();
        setBusy(true);
        setStatus(text('Lieferland wird aktualisiert …', 'Updating delivery country …'));
        if (countrySelect.form && typeof countrySelect.form.requestSubmit === 'function') {
          countrySelect.form.requestSubmit();
        } else if (countrySelect.form) {
          countrySelect.form.submit();
        }
      });
    }

    document.addEventListener('click', (event) => {
      if (
        event.target instanceof Node
        && !selector.contains(event.target)
        && !(detailsPanel && detailsPanel.contains(event.target))
      ) {
        closeDetails();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && detailsPanel && !detailsPanel.hidden) {
        event.preventDefault();
        closeDetails({ restoreFocus: true });
      }
    });

    const repositionOpenDetails = () => {
      if (!activeDetailsTrigger) return;
      const triggerRect = activeDetailsTrigger.getBoundingClientRect();
      if (
        triggerRect.bottom <= 0
        || triggerRect.top >= window.innerHeight
      ) {
        closeDetails();
        return;
      }
      positionDetails(activeDetailsTrigger);
    };

    window.addEventListener('resize', repositionOpenDetails, { passive: true });
    window.addEventListener('scroll', repositionOpenDetails, { passive: true });

    if (typeof headerMedia.addEventListener === 'function') {
      headerMedia.addEventListener('change', mountSelector);
    } else {
      headerMedia.addListener(mountSelector);
    }

    let jqueryTaxEventsBound = false;
    const bindJQueryTaxEvents = () => {
      const taxJQuery = window.jQuery_T4NT || window.jQuery;
      if (jqueryTaxEventsBound || !taxJQuery) return false;
      jqueryTaxEventsBound = true;
      taxJQuery(document).on(
        'add:cart:success.mpTax cart:updated.mpTax add:cart:upsell.mpTax',
        () => scheduleSync(false)
      );
      taxJQuery(document).on(
        'variant:changed.mpTax',
        (event) => {
          const eventRoot = event.target instanceof Element ? event.target : document;
          processTaxContent(eventRoot);
        }
      );
      taxJQuery(document).on(
        'currency:update.mpTax sidebar:updated.mpTax modalt4s:opened.mpTax',
        () => processTaxContent(document)
      );
      return true;
    };
    if (!bindJQueryTaxEvents()) {
      document.addEventListener('DOMContentLoaded', bindJQueryTaxEvents, { once: true });
    }

    const submitCheckoutFromCartPage = (targetUrl) => {
      if (!onCartPage) return false;

      const lines = Array.from(document.querySelectorAll('[data-cart-item]'));
      if (!lines.length) return false;

      const taxProfile = {
        hasGross: false,
        hasTwin: false,
        hasUnmapped: false,
        mappedCount: 0,
      };

      for (const line of lines) {
        hydrateTaxRules(line);
        const variantId = normalizedId(line.getAttribute('data-mp-tax-variant-id'));
        const mapping = byVariant.get(variantId);
        if (!mapping || variantId !== desiredVariantId(mapping)) return false;

        taxProfile.mappedCount += 1;
        if (isGrossInclusive(mapping)) {
          taxProfile.hasGross = true;
        } else {
          taxProfile.hasTwin = true;
        }
      }

      const form = document.createElement('form');
      form.hidden = true;
      form.method = 'post';
      form.action = route('cart/update');

      const addInput = (name, value) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = String(value);
        form.appendChild(input);
      };

      Object.entries(cartAttributesForProfile(taxProfile)).forEach(([key, value]) => {
        addInput(`attributes[${key}]`, value);
      });

      try {
        const checkoutUrl = new URL(targetUrl || route('checkout'), window.location.origin);
        const returnTo = checkoutUrl.origin === window.location.origin
          ? `${checkoutUrl.pathname}${checkoutUrl.search}${checkoutUrl.hash}`
          : route('checkout');
        addInput('return_to', returnTo);
        document.body.appendChild(form);
        HTMLFormElement.prototype.submit.call(form);
        return true;
      } catch (error) {
        form.remove();
        return false;
      }
    };

    const beginCheckout = (event, targetUrl) => {
      if (checkoutInFlight) {
        event.preventDefault();
        return true;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      checkoutInFlight = true;

      if (submitCheckoutFromCartPage(targetUrl)) return true;

      syncCart({ openDrawer: false, announce: true, checkout: true })
        .then(() => {
          window.location.assign(targetUrl || route('checkout'));
        })
        .catch(() => {
          checkoutInFlight = false;
        });

      return true;
    };

    window.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const cartDrawerControl = target.closest('[data-drawer-options*="#t4s-mini_cart"]');
      if (cartDrawerControl && knownCartItemCount > 0 && !syncPromise) {
        scheduleSync(false);
      }

      const cartChangeControl = target.closest(
        'a[href*="/cart/change"], [data-cart-remove], [data-action="remove"], button[name="update"]'
      );
      if (cartChangeControl) {
        [260, 900, 1800].forEach((delay) => {
          window.setTimeout(() => scheduleSync(false), delay);
        });
      }

      const addControl = target.closest(
        'form[action*="/cart/add"] button[type="submit"], form[action*="/cart/add"] input[type="submit"]'
      );
      if (addControl && beginMappedAdd(event, addControl.form)) return;

      const directAddControl = target.closest(
        'a[href*="/cart/add"], [data-action-atc][data-variant-id]'
      );
      if (directAddControl && beginMappedDirectAdd(event, directAddControl)) return;

      const checkoutControl = target.closest(
        'button[name="checkout"], input[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"]'
      );
      if (!checkoutControl) return;

      const targetUrl = checkoutControl.tagName === 'A'
        ? checkoutControl.href
        : route('checkout');
      beginCheckout(event, targetUrl);
    }, true);

    window.addEventListener('submit', (event) => {
      const form = event.target;
      const submitter = event.submitter;
      if (!(form instanceof HTMLFormElement)) return;

      if (/\/cart\/add(?:\.js)?(?:$|\?)/.test(form.action || '')) {
        if (beginMappedAdd(event, form)) return;
      }

      const isCartForm = /\/cart(?:$|\?)/.test(form.action || '');
      const isCheckoutSubmit = submitter && submitter.name === 'checkout';
      if (isCartForm && isCheckoutSubmit) beginCheckout(event, route('checkout'));
    }, true);

    document.addEventListener('change', (event) => {
      if (event.target.matches('form[action*="/cart"] [name="updates[]"], [data-cart-quantity]')) {
        [260, 900, 1800].forEach((delay) => {
          window.setTimeout(() => scheduleSync(false), delay);
        });
      }

      if (event.target.matches('select[name="ecom-product-upsell-select"]')) {
        [0, 120, 500].forEach((delay) => {
          window.setTimeout(scheduleVisualPriceRefresh, delay);
        });
      }

      if (!event.target.matches('form[action*="/cart/add"] [name="id"]')) return;
      applyDynamicCheckoutGuards(event.target.form);
      scheduleVisualPriceRefresh();
    }, true);

    ['variant:changed', 'currency:update', 'sidebar:updated'].forEach((eventName) => {
      document.addEventListener(eventName, (event) => {
        const eventRoot = event.target instanceof Element ? event.target : document;
        processTaxContent(eventRoot);
      });
    });

    };

    const dynamicTaxContentSelector = [
      '[data-mp-tax-card]',
      '[data-mp-tax-batch-url]',
      '[data-mp-tax-rule]',
      'script[type="application/json"][data-mp-tax-rules]',
      'form[action*="/cart/add"]',
    ].join(', ');

    const containsDynamicTaxContent = (node) => Boolean(
      node instanceof Element
      && (
        node.matches(dynamicTaxContentSelector)
        || node.querySelector(dynamicTaxContentSelector)
      )
    );

    const processTaxContent = (scope = document) => {
      hydrateTaxRules(scope);
      applyDynamicCheckoutGuards(scope);
      renderProductPrices();
      prepareCollectionPriceState(scope);
      renderProductCardPrices(scope);
      scheduleCollectionRuleBatches(scope);
      if (latestCart && cartTaxProfile(latestCart).mappedCount > 0) renderCartPrices(latestCart);
    };

    const observeDynamicTaxContent = () => {
      if (!('MutationObserver' in window)) return;
      const roots = [
        document.getElementById('MainContent'),
        document.getElementById('t4s-append-component'),
      ].filter((root, index, allRoots) => root && allRoots.indexOf(root) === index);
      if (!roots.length) return;
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (containsDynamicTaxContent(node)) processTaxContent(node);
          });
        });
      });
      roots.forEach((root) => observer.observe(root, { childList: true, subtree: true }));
      window.setTimeout(() => observer.disconnect(), 8000);
    };

    const observeInitialTrustedRating = () => {
      if (!trustedLink || !document.body || !('MutationObserver' in window)) return;
      const observer = new MutationObserver(() => {
        hydrateTrustedRating();
        if (trustedLink.dataset.mpTrustedSignature) observer.disconnect();
      });
      observer.observe(document.body, { childList: true, subtree: true });
      window.setTimeout(() => observer.disconnect(), 8000);
    };

    let deferredRuntimeStarted = false;
    let deferredFirstFrame = 0;
    let deferredSecondFrame = 0;
    let deferredIdleHandle = 0;
    let deferredTimeoutHandle = 0;
    const deferredIntentEvents = ['pointerover', 'pointerdown', 'focusin', 'keydown', 'click'];
    const deferredRuntimeEvents = [
      'mp:tax-content-ready',
      'shopify:section:load',
      'cart:refresh:opend',
      'cart:refresh',
    ];

    const removeDeferredRuntimeGates = () => {
      deferredIntentEvents.forEach((eventName) => {
        document.removeEventListener(eventName, forceDeferredRuntime, true);
      });
    };

    const cancelDeferredRuntimeSchedule = () => {
      if (deferredFirstFrame) window.cancelAnimationFrame(deferredFirstFrame);
      if (deferredSecondFrame) window.cancelAnimationFrame(deferredSecondFrame);
      if (deferredIdleHandle && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(deferredIdleHandle);
      }
      if (deferredTimeoutHandle) window.clearTimeout(deferredTimeoutHandle);
      deferredFirstFrame = 0;
      deferredSecondFrame = 0;
      deferredIdleHandle = 0;
      deferredTimeoutHandle = 0;
    };

    const startDeferredRuntime = () => {
      if (deferredRuntimeStarted) return;
      deferredRuntimeStarted = true;
      removeDeferredRuntimeGates();
      cancelDeferredRuntimeSchedule();
      bindDeferredRuntimeListeners();
      hydrateTrustedRating();
      hydrateTaxRules();
      updateSelector();
      applyDynamicCheckoutGuards();
      renderProductPrices();
      prepareCollectionPriceState();
      renderProductCardPrices();
      scheduleCollectionRuleBatches();
      observeDynamicTaxContent();
      observeInitialTrustedRating();
      window.setTimeout(scheduleVisualPriceRefresh, 600);
      if (onCartPage) window.setTimeout(() => {
        syncCart({ openDrawer: false, announce: false }).catch(() => {});
      }, 0);
    };

    function forceDeferredRuntime() {
      startDeferredRuntime();
    }

    const handleDeferredRuntimeEvent = (event) => {
      startDeferredRuntime();
      if (event.type === 'mp:tax-content-ready') {
        const root = event.detail?.root;
        processTaxContent(root instanceof Element ? root : document);
        return;
      }
      if (event.type === 'shopify:section:load') {
        mountSelector();
        processTaxContent(event.target instanceof Element ? event.target : document);
        return;
      }
      if (Date.now() >= ignoreRefreshEventsUntil) scheduleSync(false);
    };

    const scheduleDeferredRuntime = () => {
      deferredFirstFrame = window.requestAnimationFrame(() => {
        deferredFirstFrame = 0;
        deferredSecondFrame = window.requestAnimationFrame(() => {
          deferredSecondFrame = 0;
          if (typeof window.requestIdleCallback === 'function') {
            deferredIdleHandle = window.requestIdleCallback(startDeferredRuntime, { timeout: 700 });
          } else {
            deferredTimeoutHandle = window.setTimeout(startDeferredRuntime, 0);
          }
        });
      });
    };

    mountSelector();
    updateSelectorRoleButtons();
    deferredIntentEvents.forEach((eventName) => {
      document.addEventListener(eventName, forceDeferredRuntime, { capture: true, passive: true });
    });
    deferredRuntimeEvents.forEach((eventName) => {
      document.addEventListener(eventName, handleDeferredRuntimeEvent);
    });
    if (country !== 'DE' && role === 'private') startDeferredRuntime();
    else scheduleDeferredRuntime();
  })();

/* MP mobile header/runtime controller (moved from body-end inline JS). */
(() => {
    'use strict';

    if (window.MPMobileUIInitialized) return;
    window.MPMobileUIInitialized = true;

    const mobileMedia = window.matchMedia('(max-width: 767px)');
    const desktopMedia = window.matchMedia('(min-width: 1100px)');
    const mobileStateClasses = [
      'mp-mobile-header-top',
      'mp-mobile-header-hidden',
      'mp-mobile-header-compact',
    ];
    const desktopStateClasses = [
      'mp-desktop-header-top',
      'mp-desktop-header-down',
      'mp-desktop-header-up',
    ];
    const directionThreshold = 8;
    const announcementCloseBuffer = 14;
    let mobileCurrentState = '';
    let desktopCurrentState = '';
    let lastScrollY = Math.max(0, window.scrollY || 0);
    let pendingFrame = 0;
    let headerResizeObserver = null;
    let announcementAutoCloseHandled = false;
    let suppressDirectionUntil = 0;
    let desktopUpStartY = 0;
    let desktopDownStartY = 0;

    const headerElement = () => (
      document.querySelector('.t4s-section-header.has-mp-tax-selector')
    );

    const selectorElement = () => document.getElementById('mp-tax-selector');

    const announcementElement = () => (
      document.querySelector(
        '#shopify-section-announcement-bar .t4s-announcement-bar:not(.t4s-d-none)'
      )
    );

    const announcementCloseButton = () => (
      announcementElement()?.querySelector(
        '.t4s-announcement-bar__close.t4s-z-100'
      ) || null
    );

    const hiddenHeaderElement = () => (
      headerElement()?.querySelector('.t4s-header__wrapper') || null
    );

    const headerMiddleElement = () => (
      hiddenHeaderElement()?.querySelector('.t4s-section-header__mid') || null
    );

    const closeTaxDetails = () => {
      const selector = selectorElement();
      const panel = document.querySelector('[data-mp-tax-details]');
      if (!selector || !panel || panel.hidden) return;

      panel.hidden = true;
      panel.removeAttribute('data-mp-details-mode');
      selector.classList.remove('is-details-open');
      document.querySelectorAll('[aria-controls="mp-tax-details"]').forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });
    };

    const setHiddenHeaderAccessibility = (isHidden) => {
      const hiddenHeader = hiddenHeaderElement();
      if (!hiddenHeader) return;

      hiddenHeader.inert = isHidden;
      if (isHidden) {
        hiddenHeader.setAttribute('aria-hidden', 'true');
      } else {
        hiddenHeader.removeAttribute('aria-hidden');
      }
    };

    const setSelectorAccessibility = (isHidden) => {
      const selector = selectorElement();
      if (!selector) return;

      selector.inert = isHidden;
      if (isHidden) {
        selector.setAttribute('aria-hidden', 'true');
      } else {
        selector.removeAttribute('aria-hidden');
      }
    };

    const setDesktopHeaderAccessibility = (state) => {
      const hiddenHeader = hiddenHeaderElement();
      const middle = headerMiddleElement();
      if (!hiddenHeader || !middle) return;

      const wrapperIsHidden = state === 'down';
      const middleIsHidden = state === 'down' || state === 'up';

      hiddenHeader.inert = wrapperIsHidden;
      middle.inert = middleIsHidden;
      if (wrapperIsHidden) {
        hiddenHeader.setAttribute('aria-hidden', 'true');
      } else {
        hiddenHeader.removeAttribute('aria-hidden');
      }
      if (middleIsHidden) {
        middle.setAttribute('aria-hidden', 'true');
      } else {
        middle.removeAttribute('aria-hidden');
      }
    };

    const clearMobileState = (header = headerElement()) => {
      if (!header) return;
      header.classList.remove(...mobileStateClasses);
      header.style.removeProperty('--mp-mobile-selector-height');
      header.style.removeProperty('--mp-mobile-hidden-header-height');
      setHiddenHeaderAccessibility(false);
      setSelectorAccessibility(false);
      mobileCurrentState = '';
    };

    const clearDesktopState = (header = headerElement()) => {
      if (!header) return;
      header.classList.remove(...desktopStateClasses);
      header.style.removeProperty('--mp-desktop-header-mid-height');
      header.style.removeProperty('--mp-desktop-header-wrapper-height');
      setDesktopHeaderAccessibility('top');
      desktopCurrentState = '';
    };

    const measureHeaderLayout = () => {
      const header = headerElement();
      const selector = selectorElement();
      const hiddenHeader = hiddenHeaderElement();
      const middle = headerMiddleElement();
      if (!header || !selector || !hiddenHeader || !middle) return;

      if (desktopMedia.matches) {
        header.style.removeProperty('--mp-desktop-selector-height');
        const middleHeight = Math.max(
          0,
          middle.getBoundingClientRect().height || middle.offsetHeight || 0
        );
        const wrapperHeight = Math.max(
          0,
          hiddenHeader.getBoundingClientRect().height || hiddenHeader.offsetHeight || 0
        );
        const selectorNaturalBottom = Math.max(
          0,
          selector.getBoundingClientRect().bottom + Math.max(0, window.scrollY || 0)
        );
        desktopUpStartY = selectorNaturalBottom + middleHeight;
        desktopDownStartY = selectorNaturalBottom + wrapperHeight;
        if (middleHeight > 0) {
          header.style.setProperty('--mp-desktop-header-mid-height', `${middleHeight}px`);
        }
        if (wrapperHeight > 0) {
          header.style.setProperty(
            '--mp-desktop-header-wrapper-height',
            `${wrapperHeight}px`
          );
        }
        return;
      }

      if (!mobileMedia.matches) {
        const selectorHeight = Math.max(
          0,
          selector.getBoundingClientRect().height || selector.offsetHeight || 0
        );
        if (selectorHeight > 0) {
          header.style.setProperty('--mp-desktop-selector-height', `${selectorHeight}px`);
        }
        return;
      }

      header.style.removeProperty('--mp-desktop-selector-height');
      const selectorHeight = Math.max(
        0,
        Math.ceil(selector.getBoundingClientRect().height || selector.offsetHeight || 0)
      );
      const hiddenHeaderHeight = Math.max(
        0,
        Math.ceil(
          hiddenHeader.getBoundingClientRect().height || hiddenHeader.offsetHeight || 0
        )
      );
      if (selectorHeight > 0) {
        header.style.setProperty('--mp-mobile-selector-height', `${selectorHeight}px`);
      }
      if (hiddenHeaderHeight > 0) {
        header.style.setProperty('--mp-mobile-hidden-header-height', `${hiddenHeaderHeight}px`);
      }
    };

    const setMobileState = (state) => {
      const header = headerElement();
      if (!header || !mobileMedia.matches) {
        clearMobileState(header);
        return;
      }
      setHiddenHeaderAccessibility(state === 'hidden');
      setSelectorAccessibility(state !== 'top');
      if (state === mobileCurrentState) return;

      header.classList.remove(...mobileStateClasses);
      header.classList.add(`mp-mobile-header-${state}`);
      mobileCurrentState = state;
      closeTaxDetails();
    };

    const setDesktopState = (state) => {
      const header = headerElement();
      if (!header || !desktopMedia.matches) {
        clearDesktopState(header);
        return;
      }
      setDesktopHeaderAccessibility(state);
      if (state === desktopCurrentState) return;

      header.classList.remove(...desktopStateClasses);
      header.classList.add(`mp-desktop-header-${state}`);
      desktopCurrentState = state;
      closeTaxDetails();
    };

    const blockingOverlayIsOpen = () => Boolean(document.querySelector(
      '.t4s-drawer[aria-hidden="false"], '
      + '.t4s-drawer.is--open, '
      + '.t4s-modal[open], '
      + 'dialog[open]'
    ));

    const markAnnouncementClosed = () => {
      announcementAutoCloseHandled = true;
      suppressDirectionUntil = Date.now() + 800;
      window.setTimeout(() => {
        lastScrollY = Math.max(0, window.scrollY || 0);
        measureHeaderLayout();
      }, 650);
    };

    const bindAnnouncementClose = () => {
      const button = announcementCloseButton();
      if (!button || button.dataset.mpAutoCloseBound === 'true') return;
      button.dataset.mpAutoCloseBound = 'true';
      button.addEventListener('click', markAnnouncementClosed, { capture: true });
    };

    const maybeAutoCloseAnnouncement = (nextScrollY) => {
      if (announcementAutoCloseHandled || nextScrollY <= lastScrollY) return;

      const bar = announcementElement();
      if (!bar || bar.getAttribute('aria-hidden') === 'true') {
        announcementAutoCloseHandled = true;
        return;
      }

      const rect = bar.getBoundingClientRect();
      if (rect.bottom > -announcementCloseBuffer) return;

      const button = announcementCloseButton();
      if (!button) return;

      if (mobileMedia.matches) {
        setMobileState('hidden');
      }
      button.click();
      if (!announcementAutoCloseHandled) {
        markAnnouncementClosed();
      }
    };

    const updateForScroll = () => {
      pendingFrame = 0;
      const nextScrollY = Math.max(0, window.scrollY || 0);
      maybeAutoCloseAnnouncement(nextScrollY);

      if (desktopMedia.matches) {
        clearMobileState();

        if (nextScrollY <= directionThreshold) {
          lastScrollY = nextScrollY;
          setDesktopState('top');
          return;
        }
        if (blockingOverlayIsOpen()) {
          lastScrollY = nextScrollY;
          return;
        }
        if (Date.now() < suppressDirectionUntil) {
          lastScrollY = nextScrollY;
          return;
        }

        const delta = nextScrollY - lastScrollY;
        if (Math.abs(delta) < directionThreshold) return;

        lastScrollY = nextScrollY;
        if (delta > 0) {
          setDesktopState(
            nextScrollY >= desktopDownStartY ? 'down' : 'top'
          );
        } else {
          setDesktopState(
            nextScrollY >= desktopUpStartY ? 'up' : 'top'
          );
        }
        return;
      }

      if (!mobileMedia.matches) {
        clearMobileState();
        clearDesktopState();
        lastScrollY = nextScrollY;
        return;
      }

      clearDesktopState();
      if (nextScrollY <= directionThreshold) {
        lastScrollY = nextScrollY;
        setMobileState('top');
        return;
      }
      if (blockingOverlayIsOpen()) {
        lastScrollY = nextScrollY;
        return;
      }
      if (Date.now() < suppressDirectionUntil) {
        lastScrollY = nextScrollY;
        return;
      }

      const delta = nextScrollY - lastScrollY;
      if (Math.abs(delta) < directionThreshold) return;

      lastScrollY = nextScrollY;
      setMobileState(delta > 0 ? 'hidden' : 'compact');
    };

    const handleScroll = () => {
      if (pendingFrame) return;
      pendingFrame = window.requestAnimationFrame(updateForScroll);
    };

    const initialize = () => {
      const header = headerElement();
      const selector = selectorElement();
      const hiddenHeader = hiddenHeaderElement();
      const middle = headerMiddleElement();
      if (!header || !selector || !hiddenHeader || !middle) return;
      bindAnnouncementClose();

      if (headerResizeObserver) {
        headerResizeObserver.disconnect();
        headerResizeObserver = null;
      }

      measureHeaderLayout();
      if (typeof ResizeObserver === 'function') {
        headerResizeObserver = new ResizeObserver(measureHeaderLayout);
        headerResizeObserver.observe(selector);
        if (desktopMedia.matches) {
          headerResizeObserver.observe(hiddenHeader);
          headerResizeObserver.observe(middle);
        } else if (mobileMedia.matches) {
          headerResizeObserver.observe(hiddenHeader);
        }
      }

      lastScrollY = Math.max(0, window.scrollY || 0);

      if (desktopMedia.matches) {
        clearMobileState(header);
        clearDesktopState(header);
        measureHeaderLayout();
        setDesktopState(
          lastScrollY >= desktopUpStartY ? 'up' : 'top'
        );
        return;
      }

      if (!mobileMedia.matches) {
        clearMobileState(header);
        clearDesktopState(header);
        measureHeaderLayout();
        return;
      }

      clearDesktopState(header);
      setMobileState(lastScrollY <= directionThreshold ? 'top' : 'compact');
    };

    const handleMediaChange = () => {
      window.requestAnimationFrame(initialize);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', measureHeaderLayout, { passive: true });
    window.addEventListener('orientationchange', measureHeaderLayout, { passive: true });
    document.addEventListener('shopify:section:load', initialize);

    if (typeof mobileMedia.addEventListener === 'function') {
      mobileMedia.addEventListener('change', handleMediaChange);
    } else {
      mobileMedia.addListener(handleMediaChange);
    }

    if (typeof desktopMedia.addEventListener === 'function') {
      desktopMedia.addEventListener('change', handleMediaChange);
    } else {
      desktopMedia.addListener(handleMediaChange);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initialize, { once: true });
    } else {
      initialize();
    }
  })();



